/* MD
  ## Getting Your Fragments Model Information 🗒️
  ---
  A BIM model is only as valuable as the information it provides. Retrieving specific data efficiently is crucial for any workflow. In this tutorial, you'll learn how to extract and utilize data from Fragments with ease!
  
  ### 🖖 Importing our Libraries
  First things first, let's install all necessary dependencies to make this example work:
*/

import * as THREE from "three";
import * as OBC from "@thatopen/components";
import * as BUI from "@thatopen/ui";
import Stats from "stats.js";
import * as FRAGS from "@thatopen/fragments";
import { MapManager } from "./mapManager";
import { TransformControls } from "three/examples/jsm/controls/TransformControls.js";
import { createViewCubeOverlay } from "./viewCube";
import { setupClipper } from "./clipperTool";
import { createModelsTreePanel } from "./modelsTreePanel";
import { createViewerSharePanel, type ViewerShareState } from "./viewerSharePanel";
import { installEchoApi } from "./viewerEchoApi";
import {
  inferViewerModelKind,
  loadGltfFromUrl,
  loadGltfFromBuffer,
} from "./gltfViewerSupport";
import { createModelTransformController } from "./modelTransformController";
import {
  viewerCacheFetch,
  viewerCacheGet,
  viewerCacheClearAll,
  viewerCacheTotalSize,
} from "./viewerModelCache";
import {
  loadOverridesFromServer,
  saveOverridesToServer,
  deleteOverridesOnServer,
  canUseServerOverrides,
  debounce as overridesDebounce,
  type ViewerOverridesMap,
} from "./viewerOverridesApi";
import {
  captureCameraView,
  applyCameraView,
  saveCameraView,
  loadCameraView,
  clearCameraView,
  makeFolderKey,
  showCameraToast,
} from "./viewerCameraView";
import { createGhostMode, type GhostModeHandle } from "./viewerGhostMode";

/* MD
  ### 🌎 Setting up a Simple Scene
  To get started, let's set up a basic ThreeJS scene. This will serve as the foundation for our application and allow us to visualize the 3D models effectively:
*/

const components = new OBC.Components();

const worlds = components.get(OBC.Worlds);
const world = worlds.create<
  OBC.SimpleScene,
  OBC.SimpleCamera,
  OBC.SimpleRenderer
>();

world.scene = new OBC.SimpleScene(components);
world.scene.setup();
world.scene.three.background = new THREE.Color(0x0a0e27);

const container = document.getElementById("container")!;
if (!container) {
  throw new Error("找不到容器元素 #container");
}
world.renderer = new OBC.SimpleRenderer(components, container);

world.camera = new OBC.SimpleCamera(components);
world.camera.controls.setLookAt(58, 22, -25, 13, 0, 4.2); // convenient position for the model we will load

// ===== Render camera frustum (near/far) ===================================
// OBC.SimpleCamera 默認 PerspectiveCamera 的 far ≈ 1000，zoom out 一遠
// (>= ~1km in world units) model 就被 far plane 裁走，越遠消失越多。
// 我哋唔 cull、想無論幾遠都見到 model，所以把 far 推到 1,000,000、near
// 收細到 0.01，更新 projection matrix。
//
// 注意：fake culling camera (下面) 只係氹 FRAGS LOD 系統「全部 in view」
// 唔好 cull，並 不會 影響呢度真正 render 的 camera。所以兩邊都要分別
// 設大 frustum，先至 zoom out / 大場景 / 多 model group 都唔會被裁。
{
  const renderCam = world.camera.three as THREE.PerspectiveCamera;
  renderCam.near = 0.01;
  renderCam.far = 1_000_000;
  renderCam.updateProjectionMatrix();
}

components.init();

const grids = components.get(OBC.Grids);
grids.create(world);

/* MD
  :::info Do I need @thatopen/components?

  Not necessarily! While @thatopen/components simplifies the process of setting up a scene, you can always use plain ThreeJS to create your own custom scene setup. It's entirely up to your preference and project requirements! 😉

  :::

  ### 🛠️ Setting Up Fragments
  Now, let's configure the Fragments library core. This will allow us to load models effortlessly and start manipulating them with ease:
*/

// Worker file is served from /worker.mjs (copied to public directory)
const workerUrl = new URL("/worker.mjs", import.meta.url).href;
console.log('🔧 Worker URL:', workerUrl);
const fragments = new FRAGS.FragmentsModels(workerUrl);

/**
 * GLTF/GLB roots loaded into the viewer scene. Same modelId keys as the
 * URL `models=` payload; stored here separately because @thatopen/fragments
 * only manages .frag models.
 */
const viewerGltfRoots = new Map<string, THREE.Object3D>();

// ========== FAKE CAMERA SETUP (CULLING DISABLED) ==========
// Create a "fake" orthographic camera with HUGE frustum to trick Fragments
// This ensures Fragments thinks ALL elements are always in view
const fakeCullingCamera = new THREE.OrthographicCamera(
  -100000, // left   - X 軸左邊界
  100000,  // right  - X 軸右邊界
  100000,  // top    - Y 軸上邊界
  -100000, // bottom - Y 軸下邊界
  0.001,   // near   - 近裁剪面
  1000000  // far    - 遠裁剪面（極遠）
);
fakeCullingCamera.position.set(0, 50000, 0); // 位置：Y=+50000（模型上方）
fakeCullingCamera.lookAt(0, 0, 0);           // 朝向：看向原點（-Y 方向，向下）
fakeCullingCamera.updateProjectionMatrix();

console.log('✅ Created fake orthographic camera with HUGE frustum');
console.log('   Position: (0, 50000, 0)');
console.log('   Direction: pointing towards -Y (downward)');
console.log('   Frustum: X[-100000, 100000], Y[-100000, 100000], Z[0.001, 1000000]');
console.log('   This tricks Fragments into thinking ALL elements are always in view');

// Fake camera visual helpers removed for cleaner view
// ========== END FAKE CAMERA SETUP ==========

// DON'T add camera rest listener - we don't want automatic updates based on main camera
// world.camera.controls.addEventListener("rest", () => fragments.update(true));

// Once a model is available in the list, we can tell what camera to use
// in order to perform the culling and LOD operations.
// Also, we add the model to the 3D scene.
fragments.models.list.onItemSet.add(({ value: model }) => {
  // ========== USE FAKE CAMERA (CULLING DISABLED) ==========
  // Use the FAKE camera with huge frustum instead of main camera
  // This gives Fragments a camera reference (so its internal logic works)
  // But the frustum is so large that ALL elements are always considered "in view"
  model.useCamera(fakeCullingCamera);
  console.log('✅ model.useCamera(fakeCullingCamera) - using fake camera with HUGE frustum');
  
  world.scene.three.add(model.object);
  
  // ALSO disable frustum culling at Three.js level for extra safety (double insurance)
  let meshCount = 0;
  model.object.traverse((child: any) => {
    if (child.isMesh || child.isObject3D) {
      child.frustumCulled = false; // DISABLE Three.js culling
      meshCount++;
    }
  });
  
  console.log(`🔓 Frustum culling DISABLED for all ${meshCount} meshes`);
  console.log('📊 Fake camera frustum: -100000 to +100000 on all axes');
  console.log('✅ ALL elements should be visible regardless of position');
  // ========== END FAKE CAMERA USAGE ==========
  
  // At the end, you tell fragments to update so the model can be seen given
  // the initial camera position
  fragments.update(true);
});

/* MD
  ### 📂 Loading a Fragments Model
  With the core setup complete, it's time to load a Fragments model into our scene. Fragments are optimized for fast loading and rendering, making them ideal for large-scale 3D models.

  :::info Where can I find Fragment files?

  You can use the sample Fragment files available in our repository for testing. If you have an IFC model you'd like to convert to Fragments, check out the IfcImporter tutorial for detailed instructions.

  :::
*/

// ========== URL 參數解析 ==========
const urlParams = new URLSearchParams(window.location.search);
const modelsParam = urlParams.get('models');
let folderNameParam = urlParams.get('folder');
const shareIdParam = urlParams.get('share');
const sessionKeyParam = urlParams.get('session');

let modelsToLoad: Array<{
  id: string;
  name: string;
  url: string;
  fileId?: string;
  modelType?: string;
}> = [];

// State applied AFTER all models finish loading. Populated when ?share=<id>
// resolves a stored ViewerShareState; viewer.ts later calls applyViewerState
// using these values.
let pendingShareState: any | null = null;

const buildAuthBaseUrl = (): string => {
  const hostname = window.location.hostname;
  const protocol = window.location.protocol;
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'http://localhost:3002';
  }
  return `${protocol}//${hostname}`;
};

if (shareIdParam) {
  // Share-loaded viewer. We follow the same pattern as src/main_share.ts:
  //   1) Hit the public /api/share/:id to grab sceneState + sharer's userToken.
  //   2) Write sharer's token to sessionStorage so every subsequent API call
  //      (here and elsewhere) is implicitly authenticated as the sharer.
  //   3) Re-sign every modelId via /api/files/:id/url to get fresh R2 URLs
  //      (the URLs originally captured in the share are expired by now).
  console.log('🔗 [Share] Loading viewer from share id:', shareIdParam);
  try {
    const authBase = buildAuthBaseUrl();

    const shareResp = await fetch(`${authBase}/api/share/${shareIdParam}`);
    if (!shareResp.ok) {
      const t = await shareResp.text();
      throw new Error(`/api/share/${shareIdParam} HTTP ${shareResp.status}: ${t}`);
    }
    const shareJson = await shareResp.json();
    if (!shareJson?.success || !shareJson?.share?.sceneState) {
      throw new Error('Share has no sceneState');
    }
    const state = shareJson.share.sceneState;
    if (state.type !== 'viewer') {
      throw new Error(`Share type '${state.type}' is not 'viewer' — open it on the app instead`);
    }
    const userToken: string | undefined = shareJson.share.userToken;
    if (!userToken) {
      throw new Error('Share has no userToken — cannot fetch model URLs');
    }
    folderNameParam = state.folder || folderNameParam;

    // Step 2: behave like a logged-in sharer for the rest of the page lifetime.
    // Mirrors main_share.ts behaviour. Other API helpers (viewerSharePanel.ts,
    // model loaders, etc.) read from sessionStorage so they all benefit.
    try {
      sessionStorage.setItem('token', userToken);
      sessionStorage.setItem('user', JSON.stringify({
        userId: shareJson.share.userId,
        username: shareJson.share.username,
      }));
      console.log(`🔐 [Share] Adopted sharer identity: ${shareJson.share.username}`);
    } catch (e) {
      console.warn('[Share] Could not write to sessionStorage', e);
    }

    // Step 3: parallel re-sign every model URL with the sharer's token.
    // /api/files/:fileId/url expects the *MongoDB fileId*, not the FragmentsModel
    // modelId. Both are persisted in the share state.
    //
    // Older shares (created before the fileId fix) only carry `id`. As a recovery
    // path, fetch /api/models with the sharer's token and build a modelId→fileId
    // lookup so legacy shares still load without the user having to re-create them.
    const headers: HeadersInit = { Authorization: `Bearer ${userToken}` };
    const needsLegacyLookup = (state.models || []).some(
      (m: any) => !m.fileId && m.id
    );
    const legacyModelIdToFileId = new Map<string, string>();
    if (needsLegacyLookup) {
      try {
        const mr = await fetch(`${authBase}/api/models`, { headers });
        if (mr.ok) {
          const mj = await mr.json();
          if (mj?.success && Array.isArray(mj.models)) {
            for (const row of mj.models) {
              if (row?.id && row?.fileId) {
                legacyModelIdToFileId.set(String(row.id), String(row.fileId));
              }
            }
            console.log(
              `🩹 [Share] Built legacy modelId→fileId map (${legacyModelIdToFileId.size} entries)`
            );
          }
        } else {
          console.warn('[Share] /api/models lookup failed, HTTP', mr.status);
        }
      } catch (e) {
        console.warn('[Share] /api/models lookup threw', e);
      }
    }

    const t0 = performance.now();
    const results = await Promise.allSettled(
      (state.models || []).map(async (m: any) => {
        const fileIdForUrl =
          m.fileId || legacyModelIdToFileId.get(String(m.id)) || m.id;
        const r = await fetch(
          `${authBase}/api/files/${fileIdForUrl}/url?expiresIn=7200`,
          { headers }
        );
        if (!r.ok) {
          let body = '';
          try { body = await r.text(); } catch { /* noop */ }
          throw new Error(
            `HTTP ${r.status} ${r.statusText} (fileId=${fileIdForUrl})${
              body ? ' — ' + body.slice(0, 160) : ''
            }`
          );
        }
        const j = await r.json();
        if (!j?.url) throw new Error('Response had no url');
        // Keep the original modelId so visibility/clipping replay still matches.
        return {
          id: m.id,
          name: m.name || m.id,
          url: j.url,
          fileId: fileIdForUrl,
          modelType: m.modelType,
        };
      })
    );

    const signedModels: Array<{
      id: string;
      name: string;
      url: string;
      fileId?: string;
      modelType?: string;
    }> = [];
    const failures: string[] = [];
    results.forEach((res, i) => {
      const m = (state.models || [])[i];
      const tag = `${m?.name || ''} (id=${m?.id}, fileId=${m?.fileId || 'n/a'})`;
      if (res.status === 'fulfilled') {
        signedModels.push(res.value);
      } else {
        const msg = `${tag}: ${(res.reason as Error)?.message || res.reason}`;
        failures.push(msg);
        console.warn(`⚠️ [Share] Resign failed for ${tag}:`, res.reason);
      }
    });
    console.log(
      `🔑 [Share] Re-signed ${signedModels.length}/${(state.models || []).length} URLs in ${(performance.now() - t0).toFixed(0)}ms`
    );

    if (signedModels.length === 0) {
      throw new Error(
        'No model URLs could be resigned. First failure: ' +
          (failures[0] || 'unknown')
      );
    }
    modelsToLoad = signedModels;
    pendingShareState = state;
    console.log(`📦 [Share] Loaded ${signedModels.length} models from share`);

    // 立即把 share 內的相機套用到 world，覆蓋第 78 行的預設位置。
    // 不這樣的話，使用者會先看到預設 (58, 22, -25) 視角，等 models 載完
    // +200ms 才 applyViewerState 跳到 share 視角 — 視覺上像有 zoom-in 動作。
    // 提前套用後，整個載入過程相機就已在正確位置，models 直接在對的角度浮現。
    // applyViewerState 之後仍會再套一次（含 visibility / clipping），相機這次
    // 是 no-op 視覺上無變化。
    if (state?.camera?.position && state?.camera?.target) {
      try {
        const [px, py, pz] = state.camera.position;
        const [tx, ty, tz] = state.camera.target;
        (world.camera.controls as any).setLookAt(px, py, pz, tx, ty, tz, false);
        console.log('🎯 [Share] Pre-applied share camera (no zoom animation)');
      } catch (err) {
        console.warn('[Share] Pre-apply camera failed:', err);
      }
    }
  } catch (err) {
    console.error('❌ [Share] Failed to resolve share, falling back to URL params:', err);
    alert('Failed to load shared viewer: ' + (err as Error).message);
  }
}

// localStorage handoff from app.html (preferred path for any folder
// with more than a handful of models — the URL stays tiny so Safari
// and nginx don't reject it).
//
// IMPORTANT: do NOT consume the payload on first read. Viewer reload
// (F5 / Cmd+R) keeps the ?session= param, so removing the payload
// makes the next load silently fall back to the default school example.
// Instead we keep it for SESSION_TTL_MS (24h) and sweep stale keys.
const SESSION_TTL_MS = 24 * 60 * 60 * 1000;

// Sweep any old payloads from previous sessions so localStorage doesn't
// fill up. Runs once per page load; cheap (just a scan of localStorage
// keys with our prefix).
try {
  const now = Date.now();
  const toDelete: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const k = localStorage.key(i);
    if (!k || !k.startsWith('echov-viewer-payload-')) continue;
    try {
      const v = localStorage.getItem(k);
      if (!v) { toDelete.push(k); continue; }
      const p = JSON.parse(v);
      const createdAt = typeof p?.createdAt === 'number' ? p.createdAt : 0;
      if (!createdAt || now - createdAt > SESSION_TTL_MS) {
        toDelete.push(k);
      }
    } catch {
      toDelete.push(k);
    }
  }
  toDelete.forEach((k) => localStorage.removeItem(k));
  if (toDelete.length > 0) {
    console.log(`🧹 [Session] swept ${toDelete.length} stale viewer payload(s) from localStorage`);
  }
} catch (err) {
  console.warn('[Session] payload sweep failed', err);
}

let sessionPayloadExpired = false;
if (modelsToLoad.length === 0 && sessionKeyParam) {
  const storageKey = 'echov-viewer-payload-' + sessionKeyParam;
  try {
    const raw = localStorage.getItem(storageKey);
    if (raw) {
      const parsed = JSON.parse(raw);
      const createdAt = typeof parsed?.createdAt === 'number' ? parsed.createdAt : 0;
      const ageMs = createdAt ? Date.now() - createdAt : Number.MAX_SAFE_INTEGER;
      if (createdAt && ageMs <= SESSION_TTL_MS) {
        if (parsed?.folder && !folderNameParam) folderNameParam = parsed.folder;
        if (Array.isArray(parsed?.models)) {
          modelsToLoad = parsed.models;
          console.log(
            `📦 [Session] Loaded ${modelsToLoad.length} models from localStorage handoff (key=${sessionKeyParam}, age=${Math.round(ageMs / 1000)}s)`
          );
          console.log('📁 Folder:', folderNameParam);
        } else {
          console.warn('[Session] payload had no models array', parsed);
        }
      } else {
        // Stale entry — drop it and surface the expiry to the user.
        console.warn(`[Session] payload for ${sessionKeyParam} is stale (age=${Math.round(ageMs / 1000)}s), discarding`);
        localStorage.removeItem(storageKey);
        sessionPayloadExpired = true;
      }
    } else {
      console.warn(`[Session] no payload found in localStorage for key=${sessionKeyParam} — opened on a different browser/device?`);
      sessionPayloadExpired = true;
    }
  } catch (err) {
    console.error('[Session] failed to read/parse localStorage handoff:', err);
    sessionPayloadExpired = true;
  }
}

if (modelsToLoad.length === 0 && modelsParam) {
  try {
    modelsToLoad = JSON.parse(decodeURIComponent(modelsParam));
    console.log('📦 Models from URL:', modelsToLoad);
    console.log('📁 Folder:', folderNameParam);
  } catch (error) {
    console.error('❌ Failed to parse models parameter:', error);
  }
}

// If the handoff/URL gave us models WITHOUT a usable URL (the modern
// app.html no longer pre-signs — see openViewerWithPayload), re-sign
// each one server-side. Mirrors the share-flow re-sign loop so the
// rest of viewer.ts doesn't need to care where the URLs came from.
//
// IMPORTANT — skip re-sign for anything already in IndexedDB cache.
// The cache is keyed by stable fileId, so a previous visit already
// has the bytes locally; the load path below will short-circuit on
// cache hit without ever needing the signed URL. Without this filter
// a 50-file folder costs 50 wasted /api/files/:id/url calls on every
// reload even though the network never touches R2.
const _cacheUserIdForResign = (() => {
  try {
    const u = JSON.parse(sessionStorage.getItem('user') || '{}');
    return String(u?.id || u?._id || u?.email || 'anon');
  } catch {
    return 'anon';
  }
})();

const _resignCacheProbe = await Promise.all(
  modelsToLoad.map(async (m) => {
    const needsUrl =
      (!m.url || typeof m.url !== 'string' || m.url.length === 0) && !!m.fileId;
    if (!needsUrl) return { m, skip: true };
    // Same cacheKey rule as the loader below — fileId preferred, modelId fallback.
    const cacheKey = m.fileId || m.id;
    try {
      const hit = await viewerCacheGet(_cacheUserIdForResign, cacheKey);
      if (hit && hit.buffer && hit.buffer.byteLength > 0) {
        // Synthesise a sentinel URL so downstream code treats this
        // model as "ready". viewerCacheFetch will short-circuit on
        // cache hit and never actually open this URL.
        m.url = 'cache://' + cacheKey;
        return { m, skip: true };
      }
    } catch {
      /* cache lookup failed — fall through and re-sign */
    }
    return { m, skip: false };
  })
);
const modelsNeedingResign = _resignCacheProbe
  .filter((x) => !x.skip)
  .map((x) => x.m);
const cacheSkipCount = _resignCacheProbe.filter((x) => x.skip && !!x.m.fileId && (x.m.url || '').startsWith('cache://')).length;
if (cacheSkipCount > 0) {
  console.log(`💾 [Resign] Skipping ${cacheSkipCount}/${modelsToLoad.length} re-signs — already in IndexedDB cache`);
}
if (modelsNeedingResign.length > 0) {
  console.log(`🔑 [Resign] ${modelsNeedingResign.length}/${modelsToLoad.length} models lack a URL, batch-signing via POST /api/files/batch-urls ...`);
  const authBase = buildAuthBaseUrl();
  const token =
    sessionStorage.getItem('token') ||
    localStorage.getItem('token') ||
    '';
  if (!token) {
    console.warn('[Resign] No auth token in storage; signed-URL fetches will likely 401.');
  }
  const headers: HeadersInit = token
    ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
    : { 'Content-Type': 'application/json' };

  const t0 = performance.now();
  const results: PromiseSettledResult<typeof modelsNeedingResign[number]>[] = new Array(modelsNeedingResign.length);

  try {
    // ONE round-trip for every model. Server-side R2 presigner is
    // pure CPU so the entire 60-model batch resolves in <100ms.
    const fileIds = modelsNeedingResign.map((m) => m.fileId!).filter(Boolean);
    let resp = await fetch(`${authBase}/api/files/batch-urls`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ fileIds, expiresIn: 14400 }),
    });

    // One 429 retry (server rate limiter — should be very rare for
    // a single POST).
    if (resp.status === 429) {
      let retryAfterSec = 1;
      try {
        const b = await resp.clone().json();
        if (typeof b?.retryAfter === 'number') retryAfterSec = b.retryAfter;
      } catch { /* noop */ }
      await new Promise((r) => setTimeout(r, retryAfterSec * 1000 + 200));
      resp = await fetch(`${authBase}/api/files/batch-urls`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ fileIds, expiresIn: 14400 }),
      });
    }

    if (resp.status === 404) {
      // Older server without batch endpoint — fall back to per-file
      // with a tight concurrency cap.
      throw new Error('BATCH_NOT_FOUND');
    }
    if (!resp.ok) {
      const t = await resp.text().catch(() => resp.statusText);
      throw new Error(`batch-urls HTTP ${resp.status}: ${t.slice(0, 200)}`);
    }

    const data = await resp.json();
    const urls: Record<string, string> = (data?.urls && typeof data.urls === 'object') ? data.urls : {};
    const errors: Record<string, string> = (data?.errors && typeof data.errors === 'object') ? data.errors : {};

      modelsNeedingResign.forEach((m, idx) => {
        const url = m.fileId ? urls[m.fileId] : undefined;
        if (url) {
          m.url = url;
          results[idx] = { status: 'fulfilled', value: m };
        } else {
          const err = (m.fileId && errors[m.fileId]) || 'no url returned';
          results[idx] = { status: 'rejected', reason: new Error(`${m.name || m.id}: ${err}`) };
          console.warn(`⚠️ [Resign] failed for ${m.name || m.id} (fileId=${m.fileId}): ${err}`);
        }
      });
  } catch (err: any) {
    if (err?.message === 'BATCH_NOT_FOUND') {
      console.warn('[Resign] batch endpoint missing on server, falling back to per-file (slow)');
      const RESIGN_CONCURRENCY = 16;
      async function resignOne(
        m: typeof modelsNeedingResign[number],
        attempt = 0
      ): Promise<typeof m> {
        const r = await fetch(
          `${authBase}/api/files/${encodeURIComponent(m.fileId!)}/url?expiresIn=14400`,
          { headers: token ? { Authorization: `Bearer ${token}` } : {} }
        );
        if (r.status === 429 && attempt < 3) {
          const delay = 1000 + Math.random() * 200;
          await new Promise((res) => setTimeout(res, delay));
          return resignOne(m, attempt + 1);
        }
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const j = await r.json();
        if (!j?.url) throw new Error('no url');
        m.url = j.url;
        return m;
      }
      let cursor = 0;
      await Promise.all(
        Array.from({ length: Math.min(RESIGN_CONCURRENCY, modelsNeedingResign.length) }, async () => {
          while (cursor < modelsNeedingResign.length) {
            const idx = cursor++;
            try {
              results[idx] = { status: 'fulfilled', value: await resignOne(modelsNeedingResign[idx]) };
            } catch (reason) {
              results[idx] = { status: 'rejected', reason };
            }
          }
        })
      );
    } else {
      // Fatal — propagate "all failed".
      console.error('[Resign] batch sign threw', err);
      modelsNeedingResign.forEach((m, idx) => {
        results[idx] = { status: 'rejected', reason: err };
      });
    }
  }
  let resignFailures = 0;
  results.forEach((res, i) => {
    if (res.status === 'rejected') {
      resignFailures++;
      const m = modelsNeedingResign[i];
      console.warn(`⚠️ [Resign] failed for ${m.name || m.id} (fileId=${m.fileId}):`, res.reason);
    }
  });
  console.log(
    `🔑 [Resign] Done in ${(performance.now() - t0).toFixed(0)}ms — ` +
      `${results.length - resignFailures}/${results.length} succeeded`
  );
  // Drop any model that we still can't load. Better to show the others
  // than to abort the whole viewer because one signed URL failed.
  if (resignFailures > 0) {
    modelsToLoad = modelsToLoad.filter((m) => !!m.url);
    console.warn(`[Resign] proceeding with ${modelsToLoad.length} loadable models (dropped ${resignFailures})`);
  }
}

// 載入模型
let model: FRAGS.FragmentsModel;

if (modelsToLoad.length > 0) {
  console.log('📥 Loading models from URL parameters...');

  let fragSuccessCount = 0;
  let gltfSuccessCount = 0;
  let failCount = 0;
  let cacheHitCount = 0;
  const rendererThree = world.renderer!.three as THREE.WebGLRenderer;
  // Local clipping must be on BEFORE GLTF materials wire to the renderer's
  // clipping planes array. setupClipper enables it later too; doing it
  // here keeps GLTF loaded pre-clipper-init in good shape.
  rendererThree.localClippingEnabled = true;

  // User id used for cache key namespacing (so two accounts on the same
  // browser don't fight over each other's cached buffers).
  const cacheUserId = (() => {
    try {
      const u = JSON.parse(sessionStorage.getItem('user') || '{}');
      return String(u?.id || u?._id || u?.email || 'anon');
    } catch {
      return 'anon';
    }
  })();

  // ----- Loading overlay (viewer.html: #viewer-model-loading-overlay) -----
  // Coalesced via requestAnimationFrame so 50 concurrent completions
  // don't thrash layout 50× per frame.
  const overlay = document.getElementById('viewer-model-loading-overlay') as HTMLElement | null;
  const overlayFill = document.getElementById('viewer-model-loading-fill') as HTMLElement | null;
  const overlayPct = document.getElementById('viewer-model-loading-percent') as HTMLElement | null;
  const overlayDetail = document.getElementById('viewer-model-loading-detail') as HTMLElement | null;
  if (overlay) overlay.style.display = 'flex';
  const total = modelsToLoad.length;
  let progressDone = 0;
  let progressName = '';
  let progressRaf: number | null = null;
  const flushOverlay = () => {
    progressRaf = null;
    if (!overlay) return;
    const pct = Math.min(100, Math.round((progressDone / total) * 100));
    if (overlayFill) overlayFill.style.width = pct + '%';
    if (overlayPct) overlayPct.textContent = `${pct}%  (${progressDone}/${total})`;
    if (overlayDetail) overlayDetail.textContent = progressName || 'Loading…';
  };
  const tickProgress = (name: string) => {
    progressDone++;
    progressName = name;
    if (progressRaf === null) progressRaf = requestAnimationFrame(flushOverlay);
  };

  // ----- Load one model -----
  // Pulled out so we can call it serially for the first model (locks the
  // @thatopen/fragments world origin to a deterministic file → no random
  // F5 offset) then in parallel for the rest.
  async function loadOne(modelInfo: typeof modelsToLoad[number]): Promise<void> {
    const kind = inferViewerModelKind(modelInfo);
    const cacheKey = modelInfo.fileId || modelInfo.id;
    try {
      const { buffer, cached } = await viewerCacheFetch({
        userId: cacheUserId,
        cacheKey,
        url: modelInfo.url,
        modelType: kind,
        name: modelInfo.name,
      });
      if (cached) cacheHitCount++;

      if (kind === 'gltf') {
        const root = await loadGltfFromBuffer({
          buffer,
          url: modelInfo.url,
          modelId: modelInfo.id,
          scene: world.scene.three as THREE.Scene,
          renderer: rendererThree,
        });
        viewerGltfRoots.set(modelInfo.id, root);
        gltfSuccessCount++;
        console.log(`   ✅ GLTF placed in scene: ${modelInfo.name}`);
      } else {
        const loadedModel = await fragments.load(buffer, { modelId: modelInfo.id });
        console.log(`   ✅ Fragments loaded: ${modelInfo.name}`);
        if (!model) model = loadedModel;
        fragSuccessCount++;
      }
    } catch (error) {
      console.error(`   ❌ Failed to load ${modelInfo.name} (${kind}):`, error);
      if (kind === 'gltf') {
        try {
          console.warn('   🔁 retrying GLTF without cache helper');
          const root = await loadGltfFromUrl({
            url: modelInfo.url,
            modelId: modelInfo.id,
            scene: world.scene.three as THREE.Scene,
            renderer: rendererThree,
          });
          viewerGltfRoots.set(modelInfo.id, root);
          gltfSuccessCount++;
          return;
        } catch (e2) {
          console.error('   ❌ GLTF retry failed too:', e2);
        }
      }
      failCount++;
    }
  }

  // ----- Deterministic first, parallel rest -----
  // Sort so the "first" model is the same on every page load. Without
  // this, parallel parsing races and the @thatopen/fragments lib uses
  // whichever finished first as the world origin → random F5 offsets.
  const sorted = [...modelsToLoad].sort((a, b) =>
    (a.fileId || a.id).localeCompare(b.fileId || b.id)
  );

  console.log(`📥 Loading ${sorted.length} models — 1st serially, rest in parallel...`);
  const tLoad0 = performance.now();

  // 1) Load the first model alone — locks world origin deterministically.
  const first = sorted[0];
  console.log(`📥 [1/${sorted.length}] ${first.name} (deterministic first)`);
  await loadOne(first);
  tickProgress(first.name || '');

  // 2) Parallelise the rest with a CPU-aware concurrency cap so we don't
  //    stall the main thread parsing 50 frag files at once.
  const rest = sorted.slice(1);
  if (rest.length > 0) {
    const CONCURRENCY = Math.max(
      2,
      Math.min(6, ((navigator as any)?.hardwareConcurrency || 4) - 1)
    );
    console.log(`📥 Parallelising remaining ${rest.length} (concurrency=${CONCURRENCY})...`);
    let cursor = 0;
    await Promise.all(
      Array.from({ length: Math.min(CONCURRENCY, rest.length) }, async () => {
        while (cursor < rest.length) {
          const idx = cursor++;
          const m = rest[idx];
          console.log(`📥 [${idx + 2}/${sorted.length}] ${m.name}`);
          await loadOne(m);
          tickProgress(m.name || '');
        }
      })
    );
  }
  console.log(`📥 All model loads done in ${(performance.now() - tLoad0).toFixed(0)}ms`);

  // Ensure final paint of the bar before we hide it.
  if (progressRaf !== null) cancelAnimationFrame(progressRaf);
  progressDone = total;
  flushOverlay();
  if (overlay) {
    // Give the user a brief moment to see "100%" before fading out.
    setTimeout(() => {
      overlay.style.transition = 'opacity 300ms ease';
      overlay.style.opacity = '0';
      setTimeout(() => { overlay.style.display = 'none'; }, 320);
    }, 150);
  }

  console.log(
    `💾 [viewerCache] ${cacheHitCount}/${modelsToLoad.length} model(s) served from cache`
  );

  const successCount = fragSuccessCount + gltfSuccessCount;
  console.log(
    `📊 Load summary: ${fragSuccessCount} frag + ${gltfSuccessCount} gltf, ${failCount} failed`
  );

  // 如果所有模型都載入失敗，重定向回首頁
  if (successCount === 0 && failCount > 0) {
    console.error('❌ All models failed to load - redirecting to home page');
    alert('Failed to load models. The links may have expired. Redirecting to home page...');
    window.location.href = '/';
    throw new Error('All models failed to load');
  }

  // Intentionally NO school_arq fallback here. If the user navigated in
  // with a real folder (?session= / ?models= / ?share=) and 0 models
  // succeeded, dropping a random demo school in place would just confuse
  // them. The earlier `redirect home on all-failed` branch already
  // covered the loud case; this branch (succeeded == 0 && failed == 0)
  // is the corner case where modelsToLoad was empty after re-sign — let
  // the user see an empty world rather than an unrelated demo.
} else if (sessionPayloadExpired) {
  // ?session= was supplied but its localStorage payload is gone or expired
  // (TTL 24h, or opened on a different browser/device). Don't silently
  // load the demo — tell the user and bounce them home so they can
  // re-open from app.html.
  console.warn('⚠️ Session payload expired/missing — redirecting to home');
  alert(
    'This viewer link has expired or was opened on a different browser.\n' +
    'Please re-open the folder from the home page.'
  );
  window.location.href = '/';
  throw new Error('Viewer session payload missing');
} else {
  // True "no params at all" — keep the school example so /viewer/viewer.html
  // (no query string) still demos something. Reaching this branch is rare
  // outside of dev / direct URL pokes.
  console.log('📥 No URL parameters, loading default example...');
  const file = await fetch("https://thatopen.github.io/engine_fragment/resources/frags/school_arq.frag");
  const buffer = await file.arrayBuffer();
  console.log('📥 Downloaded default example:', buffer.byteLength, 'bytes');

  console.log('📥 Calling fragments.load()...');
  const loadPromise = fragments.load(buffer, { modelId: "example" });

  const timeoutPromise = new Promise((_, reject) => {
    setTimeout(() => reject(new Error('fragments.load() timeout after 10s')), 10000);
  });

  try {
    model = await Promise.race([loadPromise, timeoutPromise]) as FRAGS.FragmentsModel;
    console.log('📥 Default example loaded successfully');
  } catch (error) {
    console.error('❌ fragments.load() failed or timed out:', error);
    throw error;
  }
}

console.log('🎯 Model loading completed, continuing to scene check...');

// 載入完成後檢查
console.log('');
console.log('🔍 === FINAL SCENE CHECK ===');
console.log('📊 Scene children count:', world.scene.three.children.length);
console.log('📊 Fragments models count:', fragments.models.list.size);
console.log('📊 Model IDs:', Array.from(fragments.models.list.keys()));

if (model) {
  console.log('📦 Primary model:', model.modelId);
  console.log('   - Object type:', model.object.type);
  console.log('   - Children count:', model.object.children.length);
  console.log('   - Position:', model.object.position.toArray());
  console.log('   - Visible:', model.object.visible);
  console.log('   - In scene:', world.scene.three.children.includes(model.object));
} else {
  console.error('❌ No primary model set!');
}

// ========================================
// ViewCube + Zoom-to-Fit overlay (top-right of viewport)
// + Clipper (double-click create, Delete to remove)
// ========================================
try {
  // Always-fresh list of every loaded model's THREE.Object3D — both
  // FRAGS and GLTF — so ViewCube zoom-to-fit covers the whole scene.
  const getAllModelObjects = (): THREE.Object3D[] => {
    const objs: THREE.Object3D[] = [];
    fragments.models.list.forEach((m: any) => {
      if (m?.object) objs.push(m.object as THREE.Object3D);
    });
    viewerGltfRoots.forEach((root) => {
      if (root) objs.push(root);
    });
    return objs;
  };

  createViewCubeOverlay(container, world as any, getAllModelObjects);
  console.log('✅ ViewCube + Zoom-to-Fit overlay ready');

  const clipperHandle = setupClipper(components, world, container, fragments);
  (window as any).__clipperHandle = clipperHandle;
  console.log('✅ Clipper ready (double-click to create, Delete to remove, toggle panel from toolbar)');

  // ---------- Per-model XYZ transform gizmo (viewer-only) ----------
  // Stores the absolute final transform of any model the user has dragged.
  // Persisted to viewer share state so a shared link reproduces the
  // arrangement exactly. Never written back to the admin DB.
  type ViewerOverride = {
    position: { x: number; y: number; z: number };
    rotation: { x: number; y: number; z: number };
    scale: { x: number; y: number; z: number };
  };
  const viewerOverrides = new Map<string, ViewerOverride>();
  (window as any).__viewerOverrides = viewerOverrides;

  // Per-model "factory" snapshots — captured BEFORE any saved override
  // is applied. The transform gizmo's Reset button uses this to put a
  // model back to where it loaded.
  const originalTransforms = new Map<string, ViewerOverride>();

  const snapshotObject = (obj: THREE.Object3D): ViewerOverride => ({
    position: { x: obj.position.x, y: obj.position.y, z: obj.position.z },
    rotation: { x: obj.rotation.x, y: obj.rotation.y, z: obj.rotation.z },
    scale: { x: obj.scale.x, y: obj.scale.y, z: obj.scale.z },
  });

  // Walk every loaded model and remember its load-time transform. Safe
  // to call multiple times — only first sighting of a modelId sticks.
  const captureOriginalTransforms = () => {
    for (const [id, m] of fragments.models.list as Map<string, any>) {
      if (m?.object && !originalTransforms.has(id)) {
        originalTransforms.set(id, snapshotObject(m.object));
      }
    }
    for (const [id, root] of viewerGltfRoots) {
      if (root && !originalTransforms.has(id)) {
        originalTransforms.set(id, snapshotObject(root));
      }
    }
  };
  captureOriginalTransforms();
  console.log(
    `📐 [originals] captured ${originalTransforms.size} factory transform(s)`
  );

  const applyOverrideToObject = (obj: THREE.Object3D, o: ViewerOverride) => {
    obj.position.set(o.position.x, o.position.y, o.position.z);
    obj.rotation.set(o.rotation.x, o.rotation.y, o.rotation.z);
    obj.scale.set(o.scale.x, o.scale.y, o.scale.z);
    obj.updateMatrixWorld(true);
  };

  // ---- Persistence: server (MongoDB) primary, localStorage as offline fallback ----
  // Why both layers:
  //   * Server: authoritative, follows the user across browsers/devices.
  //     Lives in the `viewer_overrides` collection — fully separate from
  //     the `models` collection so app/admin model placements stay
  //     untouched.
  //   * localStorage: keeps things working offline AND covers the public
  //     share viewer (no JWT → no server access). When the server save
  //     succeeds we still mirror to localStorage for fast next-load.
  const overrideUserId = (() => {
    try {
      const u = JSON.parse(sessionStorage.getItem('user') || '{}');
      return String(u?.id || u?._id || u?.email || 'anon');
    } catch {
      return 'anon';
    }
  })();
  const overrideStorageKey = `echov:viewer:overrides:${overrideUserId}:${folderNameParam || '__nofolder__'}`;
  const overridesAuthBase = buildAuthBaseUrl();
  const overridesFolderKey = folderNameParam || '';
  const overridesServerEnabled = canUseServerOverrides();

  const writeOverridesToLocalStorage = () => {
    try {
      const obj: Record<string, ViewerOverride> = {};
      viewerOverrides.forEach((v, k) => { obj[k] = v; });
      if (Object.keys(obj).length === 0) {
        localStorage.removeItem(overrideStorageKey);
      } else {
        localStorage.setItem(overrideStorageKey, JSON.stringify(obj));
      }
    } catch (err) {
      console.warn('[overrides] localStorage write failed', err);
    }
  };

  // Debounced server PUT — gizmo drags fire many commits in quick
  // succession when the user adjusts multiple axes; one round-trip per
  // ~250 ms of "quiet" is plenty.
  const pushOverridesToServer = overridesDebounce(async () => {
    if (!overridesServerEnabled) return;
    const obj: ViewerOverridesMap = {};
    viewerOverrides.forEach((v, k) => { obj[k] = v; });
    const ok = await saveOverridesToServer(overridesAuthBase, overridesFolderKey, obj);
    console.log(
      `🛰 [overrides] server save ${ok ? '✓' : '✗'} (folder="${overridesFolderKey}", count=${Object.keys(obj).length})`
    );
  }, 250);

  const persistOverrides = () => {
    writeOverridesToLocalStorage();
    void pushOverridesToServer();
  };

  /** Apply a map of overrides into the scene; returns the count actually
   *  applied (some may belong to models not loaded in this viewer). */
  const applyOverridesMap = (map: ViewerOverridesMap | undefined | null): number => {
    if (!map) return 0;
    let n = 0;
    for (const [id, o] of Object.entries(map)) {
      if (!o) continue;
      const fragModel: any = (fragments.models.list as Map<string, any>).get(id);
      const target: THREE.Object3D | undefined =
        (fragModel && fragModel.object) || viewerGltfRoots.get(id) || undefined;
      if (!target) continue;
      applyOverrideToObject(target, o as ViewerOverride);
      viewerOverrides.set(id, o as ViewerOverride);
      n++;
    }
    if (n > 0) {
      try { fragments.update(true); } catch { /* noop */ }
    }
    return n;
  };

  const restoreOverridesFromLocalStorage = (): number => {
    try {
      const raw = localStorage.getItem(overrideStorageKey);
      if (!raw) return 0;
      return applyOverridesMap(JSON.parse(raw));
    } catch (err) {
      console.warn('[overrides] localStorage read failed', err);
      return 0;
    }
  };

  // Tolerance for "is this transform back to original?" comparison. Prevents
  // floating-point dust from leaving stale overrides after a Reset click.
  const APPROX_EPS = 1e-4;
  const isApproxOriginal = (chg: { modelId: string; position: any; rotation: any; scale: any }): boolean => {
    const orig = originalTransforms.get(chg.modelId);
    if (!orig) return false;
    const close = (a: number, b: number) => Math.abs(a - b) < APPROX_EPS;
    return (
      close(chg.position.x, orig.position.x) &&
      close(chg.position.y, orig.position.y) &&
      close(chg.position.z, orig.position.z) &&
      close(chg.rotation.x, orig.rotation.x) &&
      close(chg.rotation.y, orig.rotation.y) &&
      close(chg.rotation.z, orig.rotation.z) &&
      close(chg.scale.x, orig.scale.x) &&
      close(chg.scale.y, orig.scale.y) &&
      close(chg.scale.z, orig.scale.z)
    );
  };

  const transformController = createModelTransformController({
    world,
    onChange: (chg) => {
      // Mid-drag updates always store the current transform — even if it
      // momentarily passes through "original", we don't want to lose it.
      viewerOverrides.set(chg.modelId, {
        position: { ...chg.position },
        rotation: { ...chg.rotation },
        scale: { ...chg.scale },
      });
      try { fragments.update(true); } catch { /* noop */ }
    },
    onChangeCommit: (chg) => {
      // On commit (drag release / input change / Reset click) clean up
      // overrides that match the original — keeps the DB tidy and means
      // the next reload skips an unnecessary apply step.
      if (isApproxOriginal(chg)) {
        viewerOverrides.delete(chg.modelId);
        // Also forget on the server so cross-device reloads come back
        // to the load-time placement.
        if (overridesServerEnabled) {
          void deleteOverridesOnServer(overridesAuthBase, overridesFolderKey, chg.modelId);
        }
      }
      // Persist as soon as the user releases the gizmo so reload restores
      // the new position. Server is authoritative; localStorage mirrors
      // for offline / public-share viewers. Never writes back to the
      // admin /api/models doc, so the app/map view stays independent.
      persistOverrides();
      console.log(
        `🛠 [transform] ${chg.modelId} → pos=(${chg.position.x.toFixed(2)}, ${chg.position.y.toFixed(2)}, ${chg.position.z.toFixed(2)}) ` +
          `rot=(${chg.rotation.x.toFixed(3)}, ${chg.rotation.y.toFixed(3)}, ${chg.rotation.z.toFixed(3)}) ` +
          `scale=(${chg.scale.x.toFixed(3)}, ${chg.scale.y.toFixed(3)}, ${chg.scale.z.toFixed(3)}) ` +
          `[server=${overridesServerEnabled ? 'on' : 'off'}, localKey=${overrideStorageKey}]`
      );
    },
  });
  (window as any).__transformController = transformController;

  // Restore overrides — must run AFTER all models loaded.
  // Order: localStorage first (instant), then server overlay (authoritative).
  const localCount = restoreOverridesFromLocalStorage();
  if (localCount > 0) {
    console.log(`💾 [overrides] applied ${localCount} from localStorage (instant)`);
  }
  if (overridesServerEnabled) {
    void (async () => {
      try {
        const serverMap = await loadOverridesFromServer(overridesAuthBase, overridesFolderKey);
        const serverCount = applyOverridesMap(serverMap);
        if (serverCount > 0) {
          // Mirror authoritative server state to localStorage so the next
          // cold reload is instant and matches the server.
          writeOverridesToLocalStorage();
          console.log(
            `🛰 [overrides] applied ${serverCount} from server (folder="${overridesFolderKey}") and mirrored to localStorage`
          );
        } else {
          console.log(
            `🛰 [overrides] no server-saved overrides for folder="${overridesFolderKey}"`
          );
        }
      } catch (err) {
        console.warn('[overrides] server restore failed', err);
      }
    })();
  } else {
    console.log('🛰 [overrides] server persistence disabled (no JWT — likely a public share viewer)');
  }

  // Expose helpers to the console / EchoV
  (window as any).__viewerOverridesApi = {
    save: persistOverrides,
    restoreLocal: restoreOverridesFromLocalStorage,
    /** Pull authoritative state from the server and apply (also mirrors to localStorage). */
    pullFromServer: async () => {
      if (!overridesServerEnabled) {
        console.warn('[overrides] cannot pull — no JWT');
        return 0;
      }
      const m = await loadOverridesFromServer(overridesAuthBase, overridesFolderKey);
      const n = applyOverridesMap(m);
      writeOverridesToLocalStorage();
      console.log(`🛰 [overrides] pulled ${n} from server`);
      return n;
    },
    /** Wipe stored overrides for this folder (server + local). Does not move models. */
    clearStorage: async () => {
      try { localStorage.removeItem(overrideStorageKey); } catch { /* noop */ }
      if (overridesServerEnabled) {
        await deleteOverridesOnServer(overridesAuthBase, overridesFolderKey);
      }
      viewerOverrides.clear();
      console.log(`🗑 [overrides] cleared local + server (folder="${overridesFolderKey}")`);
    },
    /** Reset a single model: drop override (server + local). */
    forget: async (modelId: string) => {
      viewerOverrides.delete(modelId);
      writeOverridesToLocalStorage();
      if (overridesServerEnabled) {
        await deleteOverridesOnServer(overridesAuthBase, overridesFolderKey, modelId);
      }
    },
    storageKey: overrideStorageKey,
    folder: overridesFolderKey,
    serverEnabled: overridesServerEnabled,
  };
  console.log(
    `✅ Per-model XYZ transform gizmo ready ` +
      `(persistence: ${overridesServerEnabled ? 'server (MongoDB) + localStorage mirror' : 'localStorage only'})`
  );

  // Console helper for the binary model cache. Lets the user inspect
  // size and wipe everything if they suspect a stale buffer.
  (window as any).__viewerCache = {
    clearAll: viewerCacheClearAll,
    totalSize: async () => {
      const s = await viewerCacheTotalSize();
      console.log(`💾 [viewerCache] total cached: ${(s / 1024 / 1024).toFixed(2)} MB`);
      return s;
    },
  };

  // Build modelId → friendly name map from the URL params (the FragmentsModel
  // itself doesn't carry the original file name, so we have to feed it in).
  const modelNamesMap = new Map<string, string>();
  for (const m of modelsToLoad) {
    if (m && m.id) modelNamesMap.set(m.id, m.name || m.id);
  }
  // Forward declare so the move callback below can refresh the panel
  // after toggling.
  let modelsTreeHandle: ReturnType<typeof createModelsTreePanel>;
  modelsTreeHandle = createModelsTreePanel({
    fragments,
    world,
    folderName: folderNameParam || 'Models',
    modelNames: modelNamesMap,
    extraRoots: viewerGltfRoots,
    refreshFragments: () => { try { fragments.update(true); } catch { /* noop */ } },
    onMoveClick: (modelId, object, displayName) => {
      // Toggle: if the gizmo is already on this model → detach. Otherwise
      // attach to the new target (replaces any previously attached one).
      if (transformController.getCurrentModelId() === modelId) {
        transformController.detach();
      } else {
        // Pass the load-time snapshot so Reset has a baseline to restore
        // (otherwise Reset would only know the transform from the first
        // time the gizmo was attached this session).
        const original = originalTransforms.get(modelId);
        transformController.attach(modelId, object, displayName, {
          originalTransform: original,
        });
      }
      try { modelsTreeHandle.refresh(); } catch { /* noop */ }
    },
    isMoveActive: (modelId) =>
      transformController.getCurrentModelId() === modelId,
  });
  (window as any).__modelsTreeHandle = modelsTreeHandle;
  console.log('✅ Models tree panel ready');

  // ---------- Viewer Share panel ----------
  const captureViewerState = (): ViewerShareState => {
    // Camera
    const camPos = new THREE.Vector3();
    const camTgt = new THREE.Vector3();
    try { (world.camera.controls as any).getPosition(camPos); } catch { /* noop */ }
    try { (world.camera.controls as any).getTarget(camTgt); } catch { /* noop */ }

    // Per-model visibility (FRAGS + GLTF roots).
    const visibility: Record<string, boolean> = {};
    fragments.models.list.forEach((m: any, id: string) => {
      visibility[id] = m?.object ? m.object.visible !== false : true;
    });
    viewerGltfRoots.forEach((root, id) => {
      visibility[id] = root.visible !== false;
    });

    // Clipping planes
    const planeArr: ViewerShareState['clippingPlanes'] = [];
    try {
      const cl = clipperHandle?.clipper;
      if (cl) {
        for (const [, plane] of cl.list) {
          const p: any = plane;
          const helper: THREE.Object3D | undefined = p?.helper;
          const normal: THREE.Vector3 = p?.normal || new THREE.Vector3(0, 1, 0);
          const pos = helper?.position || new THREE.Vector3();
          planeArr!.push({
            position: [pos.x, pos.y, pos.z],
            normal: [normal.x, normal.y, normal.z],
            enabled: !!p?.enabled,
          });
        }
      }
    } catch { /* noop */ }

    // Manual transform overrides (XYZ gizmo) — viewer-only state.
    const overrides: Record<string, ViewerOverride> = {};
    viewerOverrides.forEach((o, id) => { overrides[id] = o; });

    return {
      type: 'viewer',
      models: modelsToLoad.map((m) => ({
        id: m.id,
        name: m.name,
        fileId: m.fileId,
        modelType: m.modelType || inferViewerModelKind(m),
      })),
      folder: folderNameParam || undefined,
      camera: {
        position: [camPos.x, camPos.y, camPos.z],
        target: [camTgt.x, camTgt.y, camTgt.z],
      },
      modelVisibility: visibility,
      clippingPlanes: planeArr,
      viewerOverrides: overrides,
    };
  };

  const applyViewerState = (state: ViewerShareState) => {
    if (!state) return;
    // Camera
    if (state.camera?.position && state.camera?.target) {
      try {
        const [px, py, pz] = state.camera.position;
        const [tx, ty, tz] = state.camera.target;
        (world.camera.controls as any).setLookAt(px, py, pz, tx, ty, tz, false);
      } catch (err) {
        console.warn('[share] camera restore failed', err);
      }
    }
    // Visibility (FRAGS + GLTF roots)
    if (state.modelVisibility) {
      fragments.models.list.forEach((m: any, id: string) => {
        if (m?.object && id in state.modelVisibility!) {
          m.object.visible = !!state.modelVisibility![id];
        }
      });
      viewerGltfRoots.forEach((root, id) => {
        if (id in state.modelVisibility!) {
          root.visible = !!state.modelVisibility![id];
        }
      });
    }
    // Manual XYZ gizmo overrides — applied last so they win over any
    // intrinsic positions set by fragments-lib / GLTF authoring.
    const ovr = (state as any).viewerOverrides as
      | Record<string, ViewerOverride>
      | undefined;
    if (ovr && typeof ovr === 'object') {
      Object.entries(ovr).forEach(([id, o]) => {
        if (!o) return;
        const fragModel: any = (fragments.models.list as Map<string, any>).get(id);
        const obj: THREE.Object3D | undefined =
          (fragModel && fragModel.object) || viewerGltfRoots.get(id) || undefined;
        if (!obj) return;
        applyOverrideToObject(obj, o);
        viewerOverrides.set(id, o);
      });
      try { fragments.update(true); } catch { /* noop */ }
    }
    // Clipping planes
    if (Array.isArray(state.clippingPlanes) && state.clippingPlanes.length > 0) {
      try {
        const cl = clipperHandle?.clipper;
        if (cl) {
          for (const sp of state.clippingPlanes) {
            const normal = new THREE.Vector3(sp.normal[0], sp.normal[1], sp.normal[2]).normalize();
            const point = new THREE.Vector3(sp.position[0], sp.position[1], sp.position[2]);
            const id = cl.createFromNormalAndCoplanarPoint(world, normal, point);
            if (id) {
              const plane: any = (cl.list as any).get(id);
              if (plane) {
                try { plane.enabled = !!sp.enabled; } catch { /* noop */ }
                try { plane.visible = !!sp.enabled; } catch { /* noop */ }
              }
            }
          }
          try { fragments.update(true); } catch { /* noop */ }
        }
      } catch (err) {
        console.warn('[share] planes restore failed', err);
      }
    }
  };

  const sharePanelHandle = createViewerSharePanel({
    authBaseUrl: buildAuthBaseUrl(),
    getCurrentState: captureViewerState,
  });
  (window as any).__sharePanelHandle = sharePanelHandle;
  console.log('✅ Viewer Share panel ready');

  // ---------- EchoV global API ----------
  // Mirrors window.EchoV from app.html so external scripts can drive the viewer
  // the same way they drive the main app. Includes both local FRAGS operations
  // (visibility, color, camera, clipper) and HTTP REST wrappers for the
  // backend endpoints listed in API_DOCUMENTATION.md.
  try {
    installEchoApi({
      fragments,
      world,
      scene: world.scene.three as THREE.Scene,
      authBaseUrl: buildAuthBaseUrl(),
      modelNames: modelNamesMap,
      panelHandles: {
        clipper: clipperHandle,
        modelsTree: modelsTreeHandle,
        sharePanel: sharePanelHandle,
      },
      refreshFragments: () => {
        try { fragments.update(true); } catch { /* noop */ }
      },
      captureState: captureViewerState,
      applyState: applyViewerState,
    });
  } catch (err) {
    console.warn('⚠️ Failed to install EchoV API:', err);
  }

  // Restore share state if loaded via ?share=
  if (pendingShareState) {
    // Wait one frame so meshes/visibility flags are flushed
    setTimeout(() => {
      console.log('🔗 [Share] Applying pending share state…');
      applyViewerState(pendingShareState);
    }, 200);
  } else {
    // Auto-restore the user's last saved camera view for this model group.
    // Skipped when ?share= is present — the share already carries an
    // explicit camera and must win.
    setTimeout(() => {
      const folderKey = makeFolderKey(
        folderNameParam,
        modelsToLoad.map((m) => m.id),
      );
      const saved = loadCameraView(folderKey);
      if (saved) {
        const ok = applyCameraView(world, saved, { animate: false });
        if (ok) {
          console.log(`📷 [CameraView] Auto-restored saved view for folderKey="${folderKey}"`);
        }
      }
      // Sync the Restore button state up front.
      const restoreBtn = document.getElementById('btn-restore-view');
      if (restoreBtn) {
        if (saved) restoreBtn.removeAttribute('disabled');
        else restoreBtn.setAttribute('disabled', '');
      }
    }, 220);
  }
} catch (err) {
  console.error('❌ Failed to initialize ViewCube/Clipper overlay:', err);
}

/* MD
  ### 🗺️ Setting up Mapbox Integration
  Now let's integrate Mapbox to display the model's location on a map:
*/

/* MD
  ### 🗺️ Setting up Mapbox Integration
  Now let's integrate Mapbox to display the model's location on a map:
*/

// 初始化 Mapbox 地圖管理器
// 注意：您需要提供您的 Mapbox access token
// 可以在 https://account.mapbox.com/access-tokens/ 獲取
const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_TOKEN || "YOUR_MAPBOX_ACCESS_TOKEN";

// 如果沒有設置 token，地圖將不會初始化
let mapManager: MapManager | null = null;
let mapInitAttempts = 0;
const MAX_MAP_INIT_ATTEMPTS = 10;

// 確保 DOM 已準備好後再初始化地圖
const initMapbox = () => {
  if (mapInitAttempts >= MAX_MAP_INIT_ATTEMPTS) {
    console.error("地圖初始化失敗：已達到最大重試次數");
    return;
  }

  if (MAPBOX_TOKEN && MAPBOX_TOKEN !== "YOUR_MAPBOX_ACCESS_TOKEN") {
    try {
      // 檢查容器元素是否存在
      const mapContainer = document.getElementById("map-container");
      const sceneContainer = document.getElementById("container");
      
      if (!mapContainer || !sceneContainer) {
        mapInitAttempts++;
        if (mapInitAttempts < MAX_MAP_INIT_ATTEMPTS) {
          setTimeout(initMapbox, 300);
        } else {
          console.error("找不到地圖容器元素，地圖功能將被禁用");
        }
        return;
      }

      // 示例：設置模型位置（這裡使用示例座標，實際應用中應該從模型數據獲取）
      // 這個座標對應於示例模型的實際位置（如果知道的話）
      const modelLocation: [number, number] = [2.1734, 41.3851]; // 巴塞羅那示例座標
      mapManager = new MapManager(
        "map-container",
        "container",
        MAPBOX_TOKEN,
        modelLocation,
        15
      );

      // 強制顯示地圖容器
      mapContainer.style.display = "block";
      mapContainer.style.visibility = "visible";
      mapContainer.style.opacity = "1";
      mapContainer.style.zIndex = "1";
      
      // 設置模型在地圖上的位置
      mapManager.setModelLocation(modelLocation[0], modelLocation[1], "School Building");

      console.log("✅ Mapbox 地圖已初始化");
      console.log("📍 地圖位置:", modelLocation);
      console.log("🗺️ 地圖容器:", mapContainer);
      console.log("📐 地圖容器尺寸:", {
        width: mapContainer.offsetWidth,
        height: mapContainer.offsetHeight,
        display: window.getComputedStyle(mapContainer).display,
        visibility: window.getComputedStyle(mapContainer).visibility
      });
      console.log("💡 提示：如果看不到地圖，請檢查：");
      console.log("   1. 地圖容器是否被 3D 場景覆蓋");
      console.log("   2. 使用左下角的「地圖控制」面板切換視圖模式");
      console.log("   3. 嘗試點擊「僅地圖」按鈕查看地圖");
      console.log("   4. 在控制台執行：mapManager.setViewMode('map-only')");
    } catch (error) {
      console.error("Mapbox 初始化失敗:", error);
      console.warn("請確保已設置 VITE_MAPBOX_TOKEN 環境變數或在代碼中提供有效的 token");
    }
  } else {
    console.warn("未設置 Mapbox token，地圖功能將被禁用");
    console.warn("請設置環境變數 VITE_MAPBOX_TOKEN 或在代碼中提供 token");
  }
};

// 等待窗口完全載入後再初始化地圖
if (typeof window !== "undefined") {
  if (window.addEventListener) {
    window.addEventListener("load", () => {
      setTimeout(initMapbox, 100);
    });
  } else {
    // 如果 addEventListener 不可用，使用備用方案
    setTimeout(initMapbox, 500);
  }
}

/* MD
  ### 🤏 Setting Up Raycaster
  To enable element selection and information querying in this example, let's configure a straightforward raycasting operation for the model:
*/

const highlightMaterial: FRAGS.MaterialDefinition = {
  color: new THREE.Color("gold"),
  renderedFaces: FRAGS.RenderedFaces.TWO,
  opacity: 1,
  transparent: false,
};

let localId: number | null = null;
let clickedModelId: string | null = null;
let selectedIds: number[] = []; // Array to store multiple selected elements

// Track which model each element belongs to
const elementModelMap = new Map<number, FRAGS.FragmentsModel>();

// Track selected elements with their model info
interface SelectedElement {
  modelId: string;
  localId: number;
  model: FRAGS.FragmentsModel;
}
const selectedElements: SelectedElement[] = [];

const highlight = async () => {
  if (!localId) return;
  const elementModel = elementModelMap.get(localId) || model;
  await elementModel.highlight([localId], highlightMaterial);
};

const highlightMultiple = async (ids: number[]) => {
  if (ids.length === 0) return;
  // Group elements by model
  const modelGroups = new Map<FRAGS.FragmentsModel, number[]>();
  for (const id of ids) {
    const elementModel = elementModelMap.get(id) || model;
    if (!modelGroups.has(elementModel)) {
      modelGroups.set(elementModel, []);
    }
    modelGroups.get(elementModel)!.push(id);
  }
  // Highlight each group
  for (const [elementModel, elementIds] of modelGroups) {
    await elementModel.highlight(elementIds, highlightMaterial);
  }
};

// New function to highlight using selectedElements (with model info)
const highlightSelectedElements = async () => {
  if (selectedElements.length === 0) return;
  // Group elements by model
  const modelGroups = new Map<FRAGS.FragmentsModel, number[]>();
  for (const el of selectedElements) {
    if (!modelGroups.has(el.model)) {
      modelGroups.set(el.model, []);
    }
    modelGroups.get(el.model)!.push(el.localId);
  }
  // Highlight each group
  for (const [elementModel, elementIds] of modelGroups) {
    await elementModel.highlight(elementIds, highlightMaterial);
  }
};

const resetHighlight = async () => {
  if (!localId) return;
  const elementModel = elementModelMap.get(localId) || model;
  await elementModel.resetHighlight([localId]);
};

const resetHighlightMultiple = async (ids: number[]) => {
  if (ids.length === 0) return;
  // Group elements by model
  const modelGroups = new Map<FRAGS.FragmentsModel, number[]>();
  for (const id of ids) {
    const elementModel = elementModelMap.get(id) || model;
    if (!modelGroups.has(elementModel)) {
      modelGroups.set(elementModel, []);
    }
    modelGroups.get(elementModel)!.push(id);
  }
  // Reset each group
  for (const [elementModel, elementIds] of modelGroups) {
    await elementModel.resetHighlight(elementIds);
  }
};

// New function to reset using selectedElements (with model info)
const resetSelectedElements = async () => {
  if (selectedElements.length === 0) return;
  // Group elements by model
  const modelGroups = new Map<FRAGS.FragmentsModel, number[]>();
  for (const el of selectedElements) {
    if (!modelGroups.has(el.model)) {
      modelGroups.set(el.model, []);
    }
    modelGroups.get(el.model)!.push(el.localId);
  }
  // Reset each group
  for (const [elementModel, elementIds] of modelGroups) {
    await elementModel.resetHighlight(elementIds);
  }
};

let onItemSelected = () => {};
let onItemDeselected = () => {};
let onMultipleItemsSelected = () => {};

// Create click dialog element
const clickDialog = document.createElement('div');
clickDialog.id = 'click-dialog';
clickDialog.style.cssText = `
  position: fixed;
  background: rgba(10, 14, 39, 0.95);
  border: 1px solid #44d3de;
  color: #a0f0f5;
  padding: 0.75rem;
  border-radius: 6px;
  font-size: 0.85rem;
  pointer-events: auto;
  z-index: 10000;
  display: none;
  min-width: 200px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.5);
  border: 1px solid #555;
`;
document.body.appendChild(clickDialog);

// Track current dialog element and its 3D position
let dialogElementId: number | null = null;
let dialogElement3DPosition: THREE.Vector3 | null = null;
// Plus the model the element belongs to — needed so the Zoom-to-Fit
// button can ask fragments for the element's bounding box.
let dialogElementModel: FRAGS.FragmentsModel | null = null;

// Function to update dialog position based on 3D element position
const updateDialogPosition = () => {
  if (!dialogElement3DPosition || !isDialogEnabled || clickDialog.style.display === 'none') {
    return;
  }
  
  // Project 3D position to screen coordinates
  const screenPosition = dialogElement3DPosition.clone();
  screenPosition.project(world.camera.three);
  
  // Convert to pixel coordinates
  const canvas = world.renderer.three.domElement;
  const x = (screenPosition.x * 0.5 + 0.5) * canvas.clientWidth;
  const y = (screenPosition.y * -0.5 + 0.5) * canvas.clientHeight;
  
  // Position dialog with offset
  clickDialog.style.left = `${x + 20}px`;
  clickDialog.style.top = `${y - 20}px`;
  
  requestAnimationFrame(updateDialogPosition);
};

// Function to show click dialog
const showClickDialog = async (
  elementId: number,
  elementName: string,
  worldPosition: THREE.Vector3,
  ownerModel?: FRAGS.FragmentsModel | null
) => {
  // Check if dialog is enabled
  if (!isDialogEnabled) {
    return;
  }
  
  dialogElementId = elementId;
  dialogElement3DPosition = worldPosition.clone();
  dialogElementModel = ownerModel || null;

  clickDialog.innerHTML = `
    <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 0.5rem;">
      <div style="font-weight: bold; color: #44d3de;">Element Selected</div>
      <button id="close-dialog-btn" style="background: none; border: none; color: #44d3de; cursor: pointer; font-size: 1.2rem; padding: 0; margin: 0; line-height: 1;">×</button>
    </div>
    <div style="font-size: 0.8rem; color: #a0f0f5; margin-bottom: 0.25rem;">ID: ${elementId}</div>
    <div style="font-size: 0.8rem; color: #ddd; margin-bottom: 0.5rem;">Name: ${elementName || 'Unnamed'}</div>
    <button id="zoom-to-fit-btn" title="Fit camera to this element"
      style="
        display: inline-flex; align-items: center; gap: 6px;
        background: rgba(68, 211, 222, 0.18);
        color: #cfeef0;
        border: 1px solid rgba(68, 211, 222, 0.45);
        border-radius: 4px;
        padding: 4px 10px;
        font-size: 11px;
        cursor: pointer;
      ">
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor"
           stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
        <circle cx="11" cy="11" r="7"/>
        <line x1="21" y1="21" x2="16.5" y2="16.5"/>
      </svg>
      Zoom to Fit
    </button>
  `;
  
  // Add close button event listener
  const closeBtn = clickDialog.querySelector('#close-dialog-btn');
  if (closeBtn) {
    closeBtn.addEventListener('click', () => {
      clickDialog.style.display = 'none';
      dialogElementId = null;
      dialogElement3DPosition = null;
      dialogElementModel = null;
      console.log('💬 Dialog closed');
    });
  }

  // Zoom-to-Fit button — frame just this element. Tries the fragment
  // model's bounding-box API first (tight fit on the actual geometry);
  // falls back to a fixed sphere around the click point if the API
  // isn't available or returned nothing useful.
  const zoomBtn = clickDialog.querySelector('#zoom-to-fit-btn') as HTMLButtonElement | null;
  if (zoomBtn) {
    zoomBtn.addEventListener('mouseenter', () => {
      zoomBtn.style.background = 'rgba(68, 211, 222, 0.35)';
    });
    zoomBtn.addEventListener('mouseleave', () => {
      zoomBtn.style.background = 'rgba(68, 211, 222, 0.18)';
    });
    zoomBtn.addEventListener('click', async () => {
      const controls: any = world.camera.controls;
      let sphere: THREE.Sphere | null = null;
      try {
        if (dialogElementModel && typeof (dialogElementModel as any).getBoxes === 'function') {
          const boxes: THREE.Box3[] | undefined =
            await (dialogElementModel as any).getBoxes([elementId]);
          if (boxes && boxes.length > 0) {
            const merged = new THREE.Box3();
            for (const b of boxes) merged.union(b);
            if (!merged.isEmpty()) {
              sphere = merged.getBoundingSphere(new THREE.Sphere());
              // Pad slightly so the element isn't flush against the
              // viewport edges after fit.
              if (sphere) sphere.radius = Math.max(sphere.radius * 1.4, 0.5);
            }
          }
        }
      } catch (err) {
        console.warn('[zoom-to-fit] getBoxes failed, will fall back to point sphere', err);
      }
      // Fallback: small sphere around the click point.
      if (!sphere && dialogElement3DPosition) {
        sphere = new THREE.Sphere(dialogElement3DPosition.clone(), 5);
      }
      if (!sphere) {
        console.warn('[zoom-to-fit] no point + no bbox — cannot fit');
        return;
      }
      try {
        if (typeof controls.fitToSphere === 'function') {
          await controls.fitToSphere(sphere, true);
        } else {
          // Last-ditch: just look at the centre while keeping current distance.
          const camPos = new THREE.Vector3();
          if (typeof controls.getPosition === 'function') controls.getPosition(camPos);
          else camPos.copy(world.camera.three.position);
          await controls.setLookAt(
            camPos.x, camPos.y, camPos.z,
            sphere.center.x, sphere.center.y, sphere.center.z,
            true
          );
        }
        console.log(
          `🔍 [zoom-to-fit] element ${elementId} → ` +
          `center=(${sphere.center.x.toFixed(2)}, ${sphere.center.y.toFixed(2)}, ${sphere.center.z.toFixed(2)}), ` +
          `radius=${sphere.radius.toFixed(2)}`
        );
      } catch (err) {
        console.warn('[zoom-to-fit] camera fit failed', err);
      }
    });
  }

  clickDialog.style.display = 'block';
  
  // Start updating position
  updateDialogPosition();
  
  console.log('💬 Dialog shown for element:', elementId);
};

const mouse = new THREE.Vector2();
// Track if user is dragging transform controls
let isTransformDragging = false;

// Custom drag handlers for camera movement
container.addEventListener("mousedown", (event) => {
  // Only intercept if in camera edit mode with a selected camera
  if (!isCameraEditMode || selectedVirtualCamera === null) {
    // Not in edit mode - allow normal orbit controls
    return;
  }
  
  const mouseNDC = new THREE.Vector2(
    (event.clientX / window.innerWidth) * 2 - 1,
    -(event.clientY / window.innerHeight) * 2 + 1
  );
  
  dragRaycaster.setFromCamera(mouseNDC, world.camera.three);
  const selectedCamera = virtualCameras[selectedVirtualCamera];
  
  // Check if clicking on the marker or axes
  const intersects = dragRaycaster.intersectObjects([
    selectedCamera.marker,
    ...selectedCamera.axesHelper.children
  ], true);
  
  if (intersects.length > 0) {
    // Clicking on camera marker/axes - start drag or rotate
    isDraggingCamera = true;
    world.camera.controls.enabled = false; // Disable orbit controls
    
    if (currentTransformMode === 'translate') {
      // MOVE MODE: Set up drag plane perpendicular to camera view
      const cameraDirection = new THREE.Vector3();
      world.camera.three.getWorldDirection(cameraDirection);
      dragPlane.setFromNormalAndCoplanarPoint(
        cameraDirection,
        selectedCamera.marker.position
      );
      
      // Calculate offset from marker center to click point
      if (dragRaycaster.ray.intersectPlane(dragPlane, dragIntersection)) {
        dragOffset.copy(dragIntersection).sub(selectedCamera.marker.position);
      }
      
      console.log('🖱️ Started MOVING camera, orbit controls disabled');
    } else {
      // ROTATE MODE: Store initial mouse position and camera rotation
      rotationStartMouse.set(event.clientX, event.clientY);
      rotationStartAngles.x = selectedCamera.camera.rotation.x;
      rotationStartAngles.y = selectedCamera.camera.rotation.y;
      rotationStartAngles.z = selectedCamera.camera.rotation.z;
      
      console.log('🖱️ Started ROTATING camera, orbit controls disabled');
    }
    
    event.stopPropagation();
    event.preventDefault();
  } else {
    // Clicking elsewhere - allow orbit controls to work
    console.log('🎮 Clicked elsewhere, orbit controls should work');
  }
});

container.addEventListener("mousemove", (event) => {
  // ONLY process if actively dragging
  if (!isDraggingCamera || selectedVirtualCamera === null) {
    return; // Don't stop propagation if not dragging
  }
  
  const selectedCamera = virtualCameras[selectedVirtualCamera];
  
  if (currentTransformMode === 'translate') {
    // MOVE MODE: Translate camera position
    const mouseNDC = new THREE.Vector2(
      (event.clientX / window.innerWidth) * 2 - 1,
      -(event.clientY / window.innerHeight) * 2 + 1
    );
    
    dragRaycaster.setFromCamera(mouseNDC, world.camera.three);
    
    if (dragRaycaster.ray.intersectPlane(dragPlane, dragIntersection)) {
      // Update marker position
      const newPosition = dragIntersection.sub(dragOffset);
      selectedCamera.marker.position.copy(newPosition);
      
      // Update virtual camera position
      selectedCamera.camera.position.copy(newPosition);
      selectedCamera.camera.updateMatrixWorld();
      selectedCamera.camera.updateProjectionMatrix();
      
      // Update helper
      selectedCamera.helper.update();
      
      // Update axes helper position
      selectedCamera.axesHelper.position.copy(newPosition);
    }
  } else {
    // ROTATE MODE: Rotate camera direction
    const deltaX = event.clientX - rotationStartMouse.x;
    const deltaY = event.clientY - rotationStartMouse.y;
    
    // Convert mouse movement to rotation angles
    const rotationSpeed = 0.005;
    const newRotationY = rotationStartAngles.y - deltaX * rotationSpeed; // Horizontal rotation (yaw)
    const newRotationX = rotationStartAngles.x - deltaY * rotationSpeed; // Vertical rotation (pitch)
    
    // Clamp vertical rotation to avoid flipping
    const clampedRotationX = Math.max(-Math.PI / 2, Math.min(Math.PI / 2, newRotationX));
    
    // Apply rotation to virtual camera
    selectedCamera.camera.rotation.set(clampedRotationX, newRotationY, 0, 'YXZ');
    selectedCamera.camera.matrixWorldNeedsUpdate = true;
    selectedCamera.camera.updateMatrixWorld(true);
    selectedCamera.camera.updateProjectionMatrix();
    
    // Update helper to show new frustum direction
    selectedCamera.helper.update();
    
    // Update marker rotation to match camera
    selectedCamera.marker.rotation.copy(selectedCamera.camera.rotation);
    
    // Update axes helper rotation to match camera
    selectedCamera.axesHelper.rotation.copy(selectedCamera.camera.rotation);
    
    // Log rotation for debugging (throttled)
    if (Math.random() < 0.05) { // Only log 5% of the time to avoid spam
      console.log('🔄 Camera rotation:', {
        x: (clampedRotationX * 180 / Math.PI).toFixed(1) + '°',
        y: (newRotationY * 180 / Math.PI).toFixed(1) + '°'
      });
    }
  }
  
  // Only stop propagation when actively dragging
  event.stopPropagation();
  event.preventDefault();
});

container.addEventListener("mouseup", (event) => {
  if (isDraggingCamera) {
    const mode = currentTransformMode === 'translate' ? 'MOVING' : 'ROTATING';
    isDraggingCamera = false;
    // Re-enable orbit controls immediately
    world.camera.controls.enabled = true;
    console.log(`🖱️ Stopped ${mode} camera, orbit controls re-enabled:`, world.camera.controls.enabled);
  }
});

// Also handle mouse leaving the container
container.addEventListener("mouseleave", () => {
  if (isDraggingCamera) {
    isDraggingCamera = false;
    world.camera.controls.enabled = true;
    console.log('🖱️ Mouse left container, orbit controls re-enabled');
  }
});

// Debug: Log orbit controls state periodically
setInterval(() => {
  if (isCameraEditMode && selectedVirtualCamera !== null) {
    console.log('🔍 Debug - Orbit controls enabled:', world.camera.controls.enabled, 'isDragging:', isDraggingCamera);
  }
}, 3000);

container.addEventListener("click", async (event) => {
  // Don't process clicks if user was dragging transform controls
  if (isTransformDragging) {
    isTransformDragging = false;
    return;
  }

  // Gate element selection on "Dialog On". Camera-edit-mode still works
  // (virtual camera markers must remain clickable in that mode), so we
  // place the gate AFTER the camera-edit branch below.
  mouse.x = event.clientX;
  mouse.y = event.clientY;
  const isShiftPressed = event.shiftKey;

  // In camera edit mode, ONLY allow clicking on virtual cameras
  if (isCameraEditMode) {
    if (virtualCameras.length > 0) {
      // Check if clicking on transform controls gizmo
      if (selectedVirtualCamera !== null && selectedVirtualCamera < virtualCameras.length) {
        const selectedCamera = virtualCameras[selectedVirtualCamera];
        // Don't process click if transform controls are being used
        if (selectedCamera.transformControls.dragging) {
          return;
        }
      }
      
      const raycaster = new THREE.Raycaster();
      const mouseNDC = new THREE.Vector2(
        (event.clientX / window.innerWidth) * 2 - 1,
        -(event.clientY / window.innerHeight) * 2 + 1
      );
      raycaster.setFromCamera(mouseNDC, world.camera.three);
      
      // Check intersection with camera markers
      const cameraMarkers = virtualCameras.map(vc => vc.marker);
      const intersects = raycaster.intersectObjects(cameraMarkers, false);
      
      if (intersects.length > 0) {
        const clickedMarker = intersects[0].object;
        if (clickedMarker.userData.isVirtualCamera) {
          const cameraIndex = clickedMarker.userData.cameraIndex;
          selectVirtualCamera(cameraIndex);
        }
      } else {
        console.log('📹 Camera edit mode: Only camera markers can be selected');
      }
    }
    return; // Block all other clicks in edit mode
  }

  // 🚫 Element selection is OFF by default — user must explicitly enable
  //    "Dialog On" from the toolbar to pick elements. This prevents the
  //    pan / orbit / zoom flow from accidentally selecting and isolating
  //    things mid-navigation.
  if (!isDialogEnabled) {
    return;
  }

  // Raycast against all loaded models (GomeraX approach)
  let result: FRAGS.RaycastResult | null = null;
  let clickedModel: FRAGS.FragmentsModel | null = null;
  
  // Get all loaded models
  const allModels = Array.from(fragments.models.list.values());
  console.log(`🎯 Raycasting against ${allModels.length} loaded models`);
  
  // Collect all intersections from all models
  const allIntersections: Array<{ 
    modelId: string;
    model: FRAGS.FragmentsModel; 
    result: FRAGS.RaycastResult; 
    distance: number 
  }> = [];
  
  // Iterate through the Map to get both key (modelId) and value (model)
  for (const [modelId, currentModel] of fragments.models.list) {
    try {
      const raycastResult = await currentModel.raycast({
        camera: world.camera.three,
        mouse,
        dom: world.renderer!.three.domElement!,
      });
      
      if (raycastResult) {
        // Calculate distance from camera to hit point
        const distance = raycastResult.point 
          ? world.camera.three.position.distanceTo(raycastResult.point)
          : Infinity;
        
        allIntersections.push({
          modelId: modelId,
          model: currentModel,
          result: raycastResult,
          distance: distance
        });
        
        console.log(`✅ Hit found in model: ${modelId}, distance: ${distance.toFixed(2)}`);
      }
    } catch (error) {
      console.warn(`⚠️ Raycast failed for model ${modelId}:`, error);
    }
  }
  
  // Sort by distance and pick the closest
  if (allIntersections.length > 0) {
    allIntersections.sort((a, b) => a.distance - b.distance);
    const closest = allIntersections[0];
    result = closest.result;
    clickedModel = closest.model;
    clickedModelId = closest.modelId;
    console.log(`🎯 Using closest hit from model: ${clickedModelId}, distance: ${closest.distance.toFixed(2)}`);
  }
  
  if (result && clickedModel && clickedModelId) {
    // If in isolated mode, only allow clicking on isolated elements (check using modelId:localId)
    if (isIsolated && isolatedSet.size > 0) {
      const clickedKey = `${clickedModelId}:${result.localId}`;
      if (!isolatedSet.has(clickedKey)) {
        console.log('🔒 Isolate mode active - can only select isolated elements');
        console.log('   Clicked element:', clickedKey, '(blocked)');
        console.log('   Isolated elements:', Array.from(isolatedSet));
        await fragments.update(true);
        return;
      }
    }
    
    // Multi-select mode with Shift key
    if (isShiftPressed) {
      const clickedId = result.localId;
      const index = selectedIds.indexOf(clickedId);
      
      if (index > -1) {
        // Element already selected - deselect it
        selectedIds.splice(index, 1);
        await clickedModel.resetHighlight([clickedId]);
        
        // Remove from selectedElements array
        const elemIndex = selectedElements.findIndex(
          el => el.modelId === clickedModelId && el.localId === clickedId
        );
        if (elemIndex > -1) {
          selectedElements.splice(elemIndex, 1);
        }
        
        console.log('➖ Deselected element:', clickedId, 'from model:', clickedModelId);
        console.log('📋 Currently selected:', selectedIds);
        
        // Update localId to the last selected element, or null if none
        if (selectedIds.length > 0) {
          localId = selectedIds[selectedIds.length - 1];
        } else {
          localId = null;
          onItemDeselected();
        }
      } else {
        // Add to selection
        selectedIds.push(clickedId);
        elementModelMap.set(clickedId, clickedModel); // Track which model this element belongs to
        
        // Add to selectedElements with model info
        selectedElements.push({
          modelId: clickedModelId,
          localId: clickedId,
          model: clickedModel
        });
        
        console.log('➕ Added element to selection:', clickedId, 'from model:', clickedModelId);
        console.log('📋 Currently selected IDs:', selectedIds);
        console.log('📋 Currently selected elements (with model):', selectedElements.map(el => `${el.modelId}:${el.localId}`));
        
        await clickedModel.highlight([clickedId], highlightMaterial);
        
        // Get element name and position for dialog
        const [data] = await clickedModel.getItemsData([clickedId], { attributesDefault: true });
        const elementName = data.Name?.value || 'Unnamed';
        
        // Get 3D position from raycast result
        const worldPosition = result.point || new THREE.Vector3();
        
        // Show click dialog at element's 3D position
        showClickDialog(clickedId, elementName, worldPosition, clickedModel);
        
        localId = clickedId;
        onItemSelected();
      }
      
      onMultipleItemsSelected();
    } else {
      // Normal single selection mode
      // Reset all previous highlights using selectedElements (with model info)
      if (selectedElements.length > 0) {
        await resetSelectedElements();
      }
      
      // Clear arrays
      selectedIds = [];
      selectedElements.length = 0;
      
      if (localId !== null && localId !== result.localId) {
        await resetHighlight();
      }
      
      localId = result.localId;
      selectedIds = [localId];
      
      // Store the clicked model for later use
      model = clickedModel;
      elementModelMap.set(localId, clickedModel); // Track which model this element belongs to
      
      // Add to selectedElements with model info
      selectedElements.push({
        modelId: clickedModelId,
        localId: localId,
        model: clickedModel
      });
      
      console.log('✅ Single selection - element:', localId, 'from model:', clickedModelId);
      console.log('📋 selectedElements:', selectedElements.map(el => `${el.modelId}:${el.localId}`));
      
      // Get element name and position for dialog
      const [data] = await clickedModel.getItemsData([localId], { attributesDefault: true });
      const elementName = data.Name?.value || 'Unnamed';
      
      // Get 3D position from raycast result
      const worldPosition = result.point || new THREE.Vector3();
      
      // Show click dialog at element's 3D position
      showClickDialog(localId, elementName, worldPosition, clickedModel);
      
      onItemSelected();
      
      // Highlight the newly selected element
      await highlight();
      
      console.log('✅ Selected element:', localId);
    }
    
    // If in isolated mode, re-apply transparent material to maintain isolation
    if (isIsolated && isolatedElementIds.length > 0) {
      const allIds = await getAllElements();
      const othersIds = allIds.filter(id => !isolatedElementIds.includes(id));
      
      const transparentMaterial: FRAGS.MaterialDefinition = {
        color: new THREE.Color(0xcccccc),
        renderedFaces: FRAGS.RenderedFaces.TWO,
        opacity: 0.1,
        transparent: true,
      };
      
      // Apply transparent material to all models
      for (const m of allModels) {
        await m.highlight(othersIds, transparentMaterial);
      }
    }
    
    await fragments.update(true);
  } else {
    // Clicking empty space
    if (isIsolated) {
      console.log('🔒 Isolate mode active - click Reset to exit isolate mode');
      await fragments.update(true);
      return;
    }
    
    // Clear all selections using selectedElements (with model info)
    if (selectedElements.length > 0) {
      await resetSelectedElements();
    }
    
    // Clear arrays
    selectedIds = [];
    selectedElements.length = 0;
    
    if (localId !== null) {
      await resetHighlight();
    }
    
    localId = null;
    onItemDeselected();
    await fragments.update(true);
  }
});

/* MD
  :::info Raycasting

  If you're unfamiliar with the raycasting logic above, we recommend checking out the dedicated raycasting tutorial. It provides a detailed explanation and step-by-step guidance to help you understand how raycasting works in this context.

  :::

  ### 🗒️ Getting Item Attributes
  Great! With the raycasting setup complete, let's move on to the exciting part: extracting information. To begin, we'll create a handy function to retrieve the direct attributes of the selected item in the scene:
*/

const getAttributes = async (attributes?: string[]) => {
  if (!localId) return null;
  // This model method is the most straightforward way to get information
  // about one or multiple elements.
  // You can see the options in the API reference.
  const [data] = await model.getItemsData([localId], {
    attributesDefault: !attributes,
    attributes,
  });
  return data;
};

/* MD
  The function above is designed to retrieve all attributes if none are specified. However, if you're only interested in a specific set of attributes, you can easily pass them as parameters. Let's create a function that retrieves just the name of the selected item:
  */

const getName = async () => {
  const attributes = await getAttributes(["Name"]);
  const Name = attributes?.Name;
  if (!(Name && "value" in Name)) return null;
  return Name.value as "string";
};

/* MD
  ### 🆔 Getting Element ID
  Let's create a helper function to retrieve the element's GlobalId (IFC GUID):
*/

const getElementId = async () => {
  if (!localId) return null;
  const attributes = await getAttributes(["GlobalId"]);
  const GlobalId = attributes?.GlobalId;
  if (!(GlobalId && "value" in GlobalId)) return null;
  return GlobalId.value as string;
};

/* MD
  ### 🔗 Retrieving Item Relations
  Accessing the direct attributes of an item is useful, but the true power lies in exploring its relationships. Items can be interconnected through relations, enabling you to understand their context and associations. For instance, a level can contain walls, and walls can reference the level they belong to. These relationships are often defined in the source file from which the Fragments were converted.

  For Fragments derived from IFC files, the possible relations and their names are determined by the IFC schema. Let's create a helper function to retrieve all Property Sets (Psets) associated with the selected item, leveraging these relationships.
*/

// `IsDefinedBy` is the relationship that links property sets (psets) to the element they define.
// `DefinesOccurrence` is the relationship that links a property set to the elements that use it.
// In this case, we don't need to know the elements that have the psets (just the psets of the selected element)
// Then we don't want to get DefinesOcurrences items and that's by attributes and relations are set to false.
// For more information, please refer to the IFC schema documentation
const getItemPropertySets = async () => {
  if (!localId) return null;
  const [data] = await model.getItemsData([localId], {
    attributesDefault: false,
    attributes: ["Name", "NominalValue"],
    relations: {
      IsDefinedBy: { attributes: true, relations: true },
      DefinesOcurrence: { attributes: false, relations: false },
    },
  });
  return (data.IsDefinedBy as FRAGS.ItemData[]) ?? [];
};

/* MD
  The data returned from the function above is structured similarly to how it's stored internally in the Fragments file. However, this format might not always be regular-developer-friendly. To make it more convenient, let's create a function that formats the result into a regular object for easier consumption:
*/

const formatItemPsets = (rawPsets: FRAGS.ItemData[]) => {
  const result: Record<string, Record<string, any>> = {};
  for (const [_, pset] of rawPsets.entries()) {
    const { Name: psetName, HasProperties } = pset;
    if (!(psetName && "value" in psetName && Array.isArray(HasProperties))) continue;
    const props: Record<string, any> = {};
    for (const [_, prop] of HasProperties.entries()) {
      const { Name, NominalValue } = prop;
      if (!(Name && "value" in Name)) continue;
      if (!(NominalValue && "value" in NominalValue)) continue;
      const name = Name.value;
      const nominalValue = NominalValue.value;
      if (!(name && nominalValue !== undefined)) continue;
      props[name] = nominalValue;
    }
    result[psetName.value] = props;
  }
  return result;
};

/* MD
  ### 📊 More Data Operations
  Beyond accessing attributes and relationships, you can also retrieve the full list of categories in the model. This enables convenient operations like fetching all elements from a specific category, which is a common use case. Let's create a function to retrieve all item names from a given category:
  */

const getNamesFromCategory = async (category: string, unique = false) => {
  const categoryIds = await model.getItemsOfCategories([
    new RegExp(`^${category}$`),
  ]);
  const localIds = categoryIds[category];

  const data = await model.getItemsData(localIds, {
    attributesDefault: false,
    attributes: ["Name"],
  });

  const names = data
    .map((d) => {
      const { Name } = d;
      if (!(Name && !Array.isArray(Name))) return null;
      return Name.value;
    })
    .filter((name) => name) as string[];

  return unique ? [...new Set(names)] : names;
};

/* MD
  ### 🌐 Exploring the Spatial Structure
  The spatial structure is a fundamental aspect of any BIM model, as it defines the hierarchical relationships between elements. With Fragments, retrieving this structure is straightforward. Here's how you can do it:
  */

const getSpatialStructure = async () => {
  const result = await model.getSpatialStructure();
  return result;
};

/* MD
  Now, thanks to the spatial structure present in the model, you can perform useful operations, such as retrieving all children of a specific item. This parent/child relationship is derived from the spatial structure, so ensure the structure accurately reflects these relationships to make the following function effective:
  */

const getFirstLevelChildren = async () => {
  const categoryIds = await model.getItemsOfCategories([/BUILDINGSTOREY/]);
  const localIds = categoryIds.IFCBUILDINGSTOREY;

  const attributes = await model.getItemsData(localIds, {
    attributesDefault: false,
    attributes: ["Name"],
  });

  let firstLevelLocalId = null;

  for (const [index, data] of attributes.entries()) {
    if (!("Name" in data && "value" in data.Name)) continue;
    if (data.Name.value === "01 - Entry Level") {
      firstLevelLocalId = localIds[index];
    }
  }

  if (firstLevelLocalId === null) return null;

  const children = await model.getItemsChildren([firstLevelLocalId]);
  return children;
};

/* MD
  ### 🧱 Accessing Geometry Data
  A key reason why a FragmentsModel is highly memory-efficient is that all BufferAttributes from the geometry in ThreeJS are removed after being used to render the model in the scene. However, all the data you see in the model, including the explicit geometry used to create the meshes in the first place, is stored within the Fragments file. Fortunately, retrieving this information is straightforward in case you need it. Here's how you can do it:
*/

const getItemGeometry = async () => {
  if (!localId) return null;
  const [geometryCollection] = await model.getItemsGeometry([localId]);
  return geometryCollection;
};

/* MD
  :::info

  Keep in mind that a single item may consist of multiple geometries. This is why the model method used above returns a nested array structure: the outer array represents the collection of items, while the inner arrays contain the geometries associated with each item.

  :::

  You can combine this with retrieving items from a category to obtain the complete explicit geometry of a specific group of elements.
  */

const getGeometriesFromCategory = async (category: string) => {
  const items = await model.getItemsOfCategories([new RegExp(`^${category}$`)]);

  const localIds = Object.values(items).flat();
  const geometries = await model.getItemsGeometry(localIds);
  return { localIds, geometries };
};

/* MD
  Finally, you can easily create a new ThreeJS mesh using any geometry data retrieved from the FragmentsModel. Here's how:
*/

let meshes: THREE.Mesh[] = [];

const meshMaterial = new THREE.MeshLambertMaterial({ color: "purple" });

const createMesh = (data: FRAGS.MeshData) => {
  const { positions, indices, normals, transform } = data;
  if (!(positions && indices && normals)) return null;
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
  geometry.setAttribute("normal", new THREE.BufferAttribute(normals, 3));
  geometry.setIndex(Array.from(indices));

  const mesh = new THREE.Mesh(geometry, meshMaterial);
  mesh.applyMatrix4(transform);
  meshes.push(mesh);
  return mesh;
};

/* MD
  ### 🧩 Adding User Interface (optional)
  We will use the `@thatopen/ui` library to add some simple and cool UI elements to our app. First, we need to call the `init` method of the `BUI.Manager` class to initialize the library:
*/

BUI.Manager.init();

// 攔截並修復 SVG 屬性錯誤（在 BUI 更新時也會觸發）
const originalSetAttribute = Element.prototype.setAttribute;
Element.prototype.setAttribute = function(name: string, value: string) {
  if (this.tagName === 'svg' && (name === 'height' || name === 'width')) {
    if (typeof value === 'string' && value.includes('rem')) {
      const remValue = parseFloat(value);
      const pxValue = remValue * 16; // 1rem = 16px
      value = `${pxValue}px`;
    }
  }
  try {
    return originalSetAttribute.call(this, name, value);
  } catch (e) {
    // 如果設置失敗，嘗試修復
    if (this.tagName === 'svg' && (name === 'height' || name === 'width')) {
      const numValue = parseFloat(String(value));
      if (!isNaN(numValue)) {
        try {
          return originalSetAttribute.call(this, name, `${numValue}px`);
        } catch (e2) {
          // 完全忽略錯誤
          return;
        }
      }
    }
    // 忽略錯誤，不影響功能
    return;
  }
};

// 也攔截 setAttributeNS
const originalSetAttributeNS = Element.prototype.setAttributeNS;
if (originalSetAttributeNS) {
  Element.prototype.setAttributeNS = function(namespace: string | null, name: string, value: string) {
    if (this.tagName === 'svg' && (name === 'height' || name === 'width')) {
      if (typeof value === 'string' && value.includes('rem')) {
        const remValue = parseFloat(value);
        const pxValue = remValue * 16;
        value = `${pxValue}px`;
      }
    }
    try {
      return originalSetAttributeNS.call(this, namespace, name, value);
    } catch (e) {
      // 忽略錯誤
      return;
    }
  };
}

// 完全抑制 SVG 相關錯誤訊息 - 在 TypeScript 層面加強攔截
const originalConsoleError = console.error;
console.error = function(...args: any[]) {
  const message = args.join(' ');
  if ((message.includes('Invalid value for <svg>') || 
       message.includes('Invalid value for <svg> attribute')) &&
      (message.includes('height') || message.includes('width')) &&
      (message.includes('rem') || message.includes('1.125rem'))) {
    // 完全抑制 SVG rem 單位錯誤
    return;
  }
  return originalConsoleError.apply(console, args);
};

// 攔截全局錯誤事件
const errorHandler = (event: ErrorEvent) => {
  const message = event.message || '';
  if ((message.includes('Invalid value for <svg>') || 
       message.includes('Invalid value for <svg> attribute')) &&
      (message.includes('height') || message.includes('width')) &&
      (message.includes('rem') || message.includes('1.125rem'))) {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    return false;
  }
};
window.addEventListener('error', errorHandler, true);

// 攔截 unhandledrejection 事件
window.addEventListener('unhandledrejection', (event) => {
  const message = String(event.reason || '');
  if (message.includes('Invalid value for <svg>') && 
      (message.includes('height') || message.includes('width')) &&
      message.includes('rem')) {
    event.preventDefault();
  }
});

// 在 TypeScript 層面攔截 SVGElement 的原型方法（作為額外保護）
if (typeof SVGElement !== 'undefined') {
  const originalSVGSetAttribute = SVGElement.prototype.setAttribute;
  SVGElement.prototype.setAttribute = function(name: string, value: string) {
    if ((name === 'height' || name === 'width') && typeof value === 'string' && value.includes('rem')) {
      const remValue = parseFloat(value);
      if (!isNaN(remValue)) {
        value = (remValue * 16) + 'px';
      }
    }
    try {
      return originalSVGSetAttribute.call(this, name, value);
    } catch (e) {
      if (name === 'height' || name === 'width') {
        return; // 完全忽略錯誤
      }
      throw e;
    }
  };
}

/* MD
Now we will add some UI to handle the logic of this tutorial. For more information about the UI library, you can check the specific documentation for it!
*/

// 收集所有模型的 categories
const allCategoriesSet = new Set<string>();
for (const [modelId, currentModel] of fragments.models.list) {
  try {
    const modelCategories = await currentModel.getCategories();
    for (const cat of modelCategories) {
      allCategoriesSet.add(cat);
    }
  } catch (error) {
    console.warn(`⚠️ Could not get categories for model ${modelId}:`, error);
  }
}
const categories = Array.from(allCategoriesSet).sort();

const categoriesDropdown = BUI.Component.create<BUI.Dropdown>(
  () => BUI.html`<bim-dropdown name="categories">
    ${categories.map(
      (category) => BUI.html`<bim-option label=${category}></bim-option>`,
    )}
  </bim-dropdown>`,
);

// 修復 SVG 屬性的函數 - 更強力的版本
const fixSvgAttributes = () => {
  const fixSvg = (element: Element | Document | ShadowRoot) => {
    const svgs = element.querySelectorAll('svg');
    svgs.forEach((svg) => {
      const height = svg.getAttribute('height');
      const width = svg.getAttribute('width');
      
      if (height && height.includes('rem')) {
        const remValue = parseFloat(height);
        if (!isNaN(remValue)) {
          const pxValue = remValue * 16;
          try {
            // 先移除舊屬性，再設置新屬性
            svg.removeAttribute('height');
            svg.setAttribute('height', `${pxValue}px`);
          } catch (e) {
            // 完全忽略錯誤
          }
        }
      }
      if (width && width.includes('rem')) {
        const remValue = parseFloat(width);
        if (!isNaN(remValue)) {
          const pxValue = remValue * 16;
          try {
            svg.removeAttribute('width');
            svg.setAttribute('width', `${pxValue}px`);
          } catch (e) {
            // 完全忽略錯誤
          }
        }
      }
    });
  };
  
  // 修復文檔中的所有 SVG
  fixSvg(document);
  
  // 修復 Shadow DOM 中的 SVG
  document.querySelectorAll('bim-panel-section').forEach((section) => {
    if (section.shadowRoot) {
      fixSvg(section.shadowRoot);
    }
  });
  
  // 也修復所有自定義元素內的 Shadow DOM
  document.querySelectorAll('bim-dropdown, bim-button, bim-checkbox, bim-label').forEach((element) => {
    if (element.shadowRoot) {
      fixSvg(element.shadowRoot);
    }
  });
};

// 監聽 categories dropdown 的變化，在更新後修復 SVG
if (categoriesDropdown && categoriesDropdown.length > 0) {
  const dropdown = categoriesDropdown[0] as any;
  if (dropdown && dropdown.addEventListener) {
    dropdown.addEventListener('change', () => {
      setTimeout(() => {
        fixSvgAttributes();
      }, 50);
    });
  }
}

// 儲存當前選中元素的資訊
let currentElementInfo: {
  elementId: string | null;
  name: string | null;
  attributes: any;
  psets: any;
  localId: number | null;
  modelId: string | null;
} = {
  elementId: null,
  name: null,
  attributes: null,
  psets: null,
  localId: null,
  modelId: null,
};

/* MD
  ### 🛠️ Center Toolbar Functions
  Create Isolate and Reset functions for the toolbar:
*/

// Track isolated state
let isIsolated = false;
let isolatedElementId: number | null = null;
let isolatedElementIds: number[] = []; // Store all isolated elements (for future multi-select)
let isolatedSet = new Set<string>(); // Store isolated elements as "modelId:localId" for accurate checking
let allModelElements: number[] = [];

// Get all elements in the model (cache for performance)
const getAllElements = async () => {
  if (allModelElements.length > 0) return allModelElements;
  
  allModelElements = [];
  
  // Get elements from all loaded models
  const allModels = Array.from(fragments.models.list.values());
  for (const currentModel of allModels) {
    const allCategories = await currentModel.getCategories();
    const allElementsMap = await currentModel.getItemsOfCategories(
      allCategories.map(cat => new RegExp(`^${cat}$`))
    );
    
    for (const ids of Object.values(allElementsMap)) {
      allModelElements.push(...ids);
    }
  }
  
  return allModelElements;
};

// Isolate function - make all other elements transparent
const isolateElement = async () => {
  if (selectedElements.length === 0) {
    console.warn('No element selected for isolation');
    return;
  }
  
  console.log('🔍 Isolating elements:', selectedElements.map(el => `${el.modelId}:${el.localId}`));
  
  // Store the isolated element(s) - support multiple elements
  isolatedElementIds = [...selectedIds];
  
  // Clear and rebuild the isolatedSet with modelId:localId keys
  isolatedSet.clear();
  for (const el of selectedElements) {
    isolatedSet.add(`${el.modelId}:${el.localId}`);
  }
  
  console.log('🎯 Isolated set:', Array.from(isolatedSet));
  
  // Make all other elements transparent
  const transparentMaterial: FRAGS.MaterialDefinition = {
    color: new THREE.Color(0xcccccc),
    renderedFaces: FRAGS.RenderedFaces.TWO,
    opacity: 0.1,
    transparent: true,
  };
  
  // Process each model separately
  let totalTransparent = 0;
  
  for (const [modelId, currentModel] of fragments.models.list) {
    console.log(`🔍 Processing model: ${modelId}`);
    console.log(`   🔑 Model ID type:`, typeof modelId);
    console.log(`   🔑 Model ID value:`, JSON.stringify(modelId));
    
    // Get all elements in this model
    const modelCategories = await currentModel.getCategories();
    console.log(`   Categories in model:`, modelCategories);
    
    const modelElementsMap = await currentModel.getItemsOfCategories(
      modelCategories.map(cat => new RegExp(`^${cat}$`))
    );
    
    const modelElementIds: number[] = [];
    for (const ids of Object.values(modelElementsMap)) {
      modelElementIds.push(...ids);
    }
    
    console.log(`   Total elements in model: ${modelElementIds.length}`);
    
    // Check if any isolated elements belong to this model
    let isolatedInThisModel = 0;
    for (const key of isolatedSet) {
      if (key.startsWith(`${modelId}:`)) {
        isolatedInThisModel++;
        console.log(`   ✅ Found isolated element in this model: ${key}`);
      }
    }
    console.log(`   📊 Isolated elements in this model: ${isolatedInThisModel}`);
    
    // Filter to only non-isolated elements (check using modelId:localId)
    const modelOthersIds = modelElementIds.filter(id => {
      const key = `${modelId}:${id}`;
      const isIsolated = isolatedSet.has(key);
      if (isIsolated) {
        console.log(`   🎯 Keeping element visible: ${key}`);
      }
      return !isIsolated;
    });
    
    console.log(`   Elements to make transparent: ${modelOthersIds.length}`);
    
    if (modelOthersIds.length > 0) {
      await currentModel.highlight(modelOthersIds, transparentMaterial);
      totalTransparent += modelOthersIds.length;
      console.log(`   ✅ Made ${modelOthersIds.length} elements transparent in model: ${modelId}`);
    }
    
    // Keep frustum culling disabled
    currentModel.object.traverse((child: any) => {
      if (child.isMesh || child.isObject3D) {
        child.frustumCulled = false;
      }
    });
  }
  
  isIsolated = true;
  isolatedElementId = localId;
  
  await fragments.update(true);
  
  console.log('✅ Isolation complete!');
  console.log('   📌 Isolated elements:', Array.from(isolatedSet));
  console.log('   📊 Number of isolated elements:', selectedElements.length);
  console.log('   🔍 Total elements made transparent:', totalTransparent);
  console.log('   🔓 Frustum culling remains DISABLED');
};

// Reset function - restore all elements to normal
const resetSettings = async () => {
  console.log('🔄 Resetting all settings...');
  
  const allIds = await getAllElements();
  
  // Reset all highlights (including transparent materials) for all models
  const allModels = Array.from(fragments.models.list.values());
  for (const currentModel of allModels) {
    await currentModel.resetHighlight(allIds);
    
    // Keep frustum culling disabled (maintain fake camera behavior)
    currentModel.object.traverse((child: any) => {
      if (child.isMesh || child.isObject3D) {
        child.frustumCulled = false;
      }
    });
  }
  
  // Re-highlight currently selected elements if any (using selectedElements with model info)
  if (selectedElements.length > 0) {
    await highlightSelectedElements();
    console.log('🔆 Re-highlighted selected elements:', selectedElements.map(el => `${el.modelId}:${el.localId}`));
  }
  
  isIsolated = false;
  isolatedElementId = null;
  isolatedElementIds = [];
  isolatedSet.clear(); // Clear the modelId:localId set
  await fragments.update(true);
  
  console.log('✅ All settings reset. Total elements restored:', allIds.length);
};

// ========== 創建原生 HTML Toolbar（完全控制佈局）==========
// 創建 toolbar 容器
const createNativeToolbar = () => {
  const container = document.createElement('div');
  container.id = 'native-center-toolbar';
  container.style.cssText = `
    position: fixed;
    bottom: 20px;
    left: 50%;
    transform: translateX(-50%);
    z-index: 99999;
    background: rgba(10, 14, 39, 0.95);
    border: 1px solid #44d3de;
    border-radius: 8px;
    box-shadow: 0 0 20px rgba(68, 211, 222, 0.3);
    backdrop-filter: blur(10px);
    padding: 0.5rem;
    display: flex;
    flex-direction: row;
    gap: 0.5rem;
    align-items: center;
  `;

  // View 群組
  const viewGroup = document.createElement('div');
  viewGroup.style.cssText = `
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 0.25rem;
    padding: 0.5rem;
    background: rgba(68, 211, 222, 0.1);
    border-radius: 6px;
    border: 1px solid rgba(68, 211, 222, 0.3);
  `;
  
  const viewTitle = document.createElement('div');
  viewTitle.textContent = 'View';
  viewTitle.style.cssText = `
    font-size: 0.65rem;
    color: #44d3de;
    font-weight: 600;
    margin-right: 0.25rem;
  `;
  
  const viewButtons = document.createElement('div');
  viewButtons.style.cssText = 'display: flex; gap: 0.35rem;';
  viewButtons.innerHTML = `
    <bim-button id="btn-isolate" icon="material-symbols:visibility" label="Isolate" disabled></bim-button>
    <bim-button id="btn-reset" label="Reset" icon="material-symbols:refresh"></bim-button>
    <bim-button id="btn-dialog" icon="material-symbols:chat-bubble-outline" label="Dialog Off" tooltip="Click to enable element picking + click dialog"></bim-button>
    <bim-button id="btn-ghost" label="Ghost" icon="material-symbols:blur-on" tooltip="Ghost mode (X-ray) — toggle global semi-transparency"></bim-button>
    <bim-button id="btn-save-view" label="Save View" icon="material-symbols:bookmark-add-outline" tooltip="Save current camera view (per-model-group)"></bim-button>
    <bim-button id="btn-restore-view" label="Restore View" icon="material-symbols:bookmark-outline" tooltip="Jump back to your saved camera view" disabled></bim-button>
  `;
  
  viewGroup.appendChild(viewTitle);
  viewGroup.appendChild(viewButtons);

  // (Simulator 群組已移除；Clipper 改放到下方 Panels 群組)
  
  // Panel Control Group
  const panelGroup = document.createElement('div');
  panelGroup.style.cssText = `
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    padding: 0.5rem;
    background: rgba(68, 211, 222, 0.1);
    border-radius: 6px;
    border: 1px solid rgba(68, 211, 222, 0.3);
  `;
  
  const panelTitle = document.createElement('div');
  panelTitle.textContent = 'Panels';
  panelTitle.style.cssText = `
    font-size: 0.65rem;
    color: #44d3de;
    font-weight: 600;
    margin-right: 0.25rem;
  `;
  
  const panelButtons = document.createElement('div');
  panelButtons.style.cssText = 'display: flex; gap: 0.35rem;';
  panelButtons.innerHTML = `
    <bim-button id="btn-toggle-models-tree" label="Models" icon="material-symbols:folder-open-outline" tooltip="Toggle Models tree (show/hide + fit-to-view per model)"></bim-button>
    <bim-button id="btn-toggle-element-info" label="Element Info" icon="material-symbols:info-outline" tooltip="Toggle Selected Element Info panel"></bim-button>
    <bim-button id="btn-toggle-model-info" label="Model Info" icon="material-symbols:dashboard-outline" tooltip="Toggle Model Information panel"></bim-button>
    <bim-button id="btn-clipper-toggle" label="Clipper" icon="material-symbols:content-cut" tooltip="Toggle Clipper panel (drag header to move)"></bim-button>
    <bim-button id="btn-share-toggle" label="Share" icon="material-symbols:share" tooltip="Save / list shared viewer URLs"></bim-button>
  `;
  
  panelGroup.appendChild(panelTitle);
  panelGroup.appendChild(panelButtons);

  container.appendChild(viewGroup);
  container.appendChild(panelGroup);

  return container;
};

const nativeToolbar = createNativeToolbar();

// Share 模式（?share=<id>）下，移除以下功能：
//   - Save View / Restore View：兩者依使用者 id + folder hash 存 localStorage，
//     收件人通常是匿名且只看一次，根本用不到；而且 share 連結本身就帶相機狀態。
//   - Share：share 管理面板（產生/列出 share URL）— 接收者不應再生 share。
if (shareIdParam) {
  ['btn-save-view', 'btn-restore-view', 'btn-share-toggle'].forEach(id => {
    const el = nativeToolbar.querySelector(`#${id}`);
    if (el && el.parentElement) el.parentElement.removeChild(el);
  });
}

// 更新 toolbar 狀態的函數
const updateNativeToolbar = (state: any) => {
  const isolateBtn = document.getElementById('btn-isolate');
  const dialogBtn = document.getElementById('btn-dialog');
  const editBtn = document.getElementById('btn-edit-mode');
  const transformBtn = document.getElementById('btn-transform-mode');
  const previewBtn = document.getElementById('btn-preview');
  
  if (isolateBtn) {
    isolateBtn.setAttribute('label', state.selectionCount > 1 ? `Isolate (${state.selectionCount} selected)` : 'Isolate');
    if (state.hasSelection) {
      isolateBtn.removeAttribute('disabled');
    } else {
      isolateBtn.setAttribute('disabled', '');
    }
  }
  
  if (dialogBtn) {
    dialogBtn.setAttribute('label', state.dialogEnabled ? 'Dialog On' : 'Dialog Off');
    // ON = highlighted blue (consistent with Ghost / Models / Clipper toggles).
    // OFF = greyed so user sees clearly that picking is currently disabled.
    if (state.dialogEnabled) {
      dialogBtn.style.cssText = 'background-color: rgba(68, 211, 222, 0.35);';
    } else {
      dialogBtn.style.cssText = '--bim-label--c: #666; opacity: 0.5;';
    }
  }
  
  if (editBtn) {
    editBtn.setAttribute('label', state.cameraEditMode ? 'Edit: ON' : 'Edit: OFF');
    editBtn.style.backgroundColor = state.cameraEditMode ? 'rgba(68, 211, 222, 0.3)' : '';
  }
  
  if (transformBtn) {
    const mode = state.transformMode || 'translate';
    transformBtn.setAttribute('label', mode === 'translate' ? 'Move' : 'Rotate');
    transformBtn.setAttribute('icon', mode === 'translate' ? 'material-symbols:open-with' : 'material-symbols:rotate-90-degrees-ccw');
    const hasCameraSelected = state.cameraEditMode && state.cameraSelected;
    if (hasCameraSelected) {
      transformBtn.removeAttribute('disabled');
      transformBtn.style.backgroundColor = 'rgba(68, 211, 222, 0.2)';
    } else {
      transformBtn.setAttribute('disabled', '');
      transformBtn.style.backgroundColor = '';
    }
  }
  
  if (previewBtn) {
    const hasCameraSelected = state.cameraEditMode && state.cameraSelected;
    if (hasCameraSelected) {
      previewBtn.removeAttribute('disabled');
    } else {
      previewBtn.setAttribute('disabled', '');
    }
  }
};

// 綁定事件監聽器
setTimeout(() => {
  const isolateBtn = document.getElementById('btn-isolate');
  const resetBtn = document.getElementById('btn-reset');
  const dialogBtn = document.getElementById('btn-dialog');
  const addCamBtn = document.getElementById('btn-add-cam');
  const editBtn = document.getElementById('btn-edit-mode');
  const transformBtn = document.getElementById('btn-transform-mode');
  const removeCamBtn = document.getElementById('btn-remove-cam');
  const previewBtn = document.getElementById('btn-preview');
  const toggleElementInfoBtn = document.getElementById('btn-toggle-element-info');
  const toggleModelInfoBtn = document.getElementById('btn-toggle-model-info');
  
  if (isolateBtn) isolateBtn.addEventListener('click', async () => {
    if (selectedIds.length === 0) return;
    isolateBtn.setAttribute('loading', '');
    await isolateElement();
    isolateBtn.removeAttribute('loading');
  });
  
  if (resetBtn) resetBtn.addEventListener('click', async () => {
    resetBtn.setAttribute('loading', '');
    await resetSettings();
    resetBtn.removeAttribute('loading');
  });
  
  if (dialogBtn) dialogBtn.addEventListener('click', () => {
    isDialogEnabled = !isDialogEnabled;
    updateNativeToolbar({ 
      hasSelection: selectedIds.length > 0,
      selectionCount: selectedIds.length,
      dialogEnabled: isDialogEnabled,
      cameraEditMode: isCameraEditMode,
      cameraSelected: selectedVirtualCamera !== null,
      transformMode: currentTransformMode
    });
    console.log(`💬 Click dialog: ${isDialogEnabled ? 'enabled' : 'disabled'}`);
    if (!isDialogEnabled) {
      clickDialog.style.display = 'none';
    }
  });
  
  if (addCamBtn) addCamBtn.addEventListener('click', () => addVirtualCamera());
  
  if (editBtn) editBtn.addEventListener('click', () => {
    toggleCameraEditMode();
    updateNativeToolbar({ 
      hasSelection: selectedIds.length > 0,
      selectionCount: selectedIds.length,
      dialogEnabled: isDialogEnabled,
      cameraEditMode: isCameraEditMode,
      cameraSelected: selectedVirtualCamera !== null,
      transformMode: currentTransformMode
    });
  });
  
  if (transformBtn) transformBtn.addEventListener('click', () => {
    toggleVirtualCameraMode();
    updateNativeToolbar({ 
      hasSelection: selectedIds.length > 0,
      selectionCount: selectedIds.length,
      dialogEnabled: isDialogEnabled,
      cameraEditMode: isCameraEditMode,
      cameraSelected: selectedVirtualCamera !== null,
      transformMode: currentTransformMode
    });
  });
  
  if (removeCamBtn) removeCamBtn.addEventListener('click', () => removeLastVirtualCamera());
  if (previewBtn) previewBtn.addEventListener('click', () => toggleCameraPreview());

  // Clipper toolbar button → toggle floating clipper panel
  const clipperToggleBtn = document.getElementById('btn-clipper-toggle');
  if (clipperToggleBtn) {
    clipperToggleBtn.addEventListener('click', () => {
      const handle = (window as any).__clipperHandle;
      if (!handle) {
        console.warn('Clipper handle not ready yet');
        return;
      }
      handle.togglePanel();
      const visible = handle.isPanelVisible();
      clipperToggleBtn.style.backgroundColor = visible ? 'rgba(68, 211, 222, 0.3)' : '';
    });
  }

  // Models tree toolbar button
  const modelsTreeBtn = document.getElementById('btn-toggle-models-tree');
  if (modelsTreeBtn) {
    modelsTreeBtn.addEventListener('click', () => {
      const handle = (window as any).__modelsTreeHandle;
      if (!handle) {
        console.warn('Models tree handle not ready yet');
        return;
      }
      handle.togglePanel();
      const visible = handle.isPanelVisible();
      modelsTreeBtn.style.backgroundColor = visible ? 'rgba(68, 211, 222, 0.3)' : '';
    });
  }

  // Share toolbar button → toggle Shared Views panel
  const shareToggleBtn = document.getElementById('btn-share-toggle');
  if (shareToggleBtn) {
    shareToggleBtn.addEventListener('click', () => {
      const handle = (window as any).__sharePanelHandle;
      if (!handle) {
        console.warn('Share panel handle not ready yet');
        return;
      }
      handle.togglePanel();
      const visible = handle.isPanelVisible();
      shareToggleBtn.style.backgroundColor = visible ? 'rgba(68, 211, 222, 0.3)' : '';
    });
  }
  
  // Panel toggle buttons — Element Info / Model Info default to hidden;
  // background colour mirrors the active state of the other panel toggles.
  if (toggleElementInfoBtn) {
    toggleElementInfoBtn.addEventListener('click', () => {
      const panel = document.getElementById('native-element-info-panel');
      if (panel) {
        const willShow = panel.style.display === 'none';
        panel.style.display = willShow ? 'flex' : 'none';
        toggleElementInfoBtn.style.backgroundColor = willShow ? 'rgba(68, 211, 222, 0.3)' : '';
      }
    });
  }

  if (toggleModelInfoBtn) {
    toggleModelInfoBtn.addEventListener('click', () => {
      const panel = document.getElementById('native-model-info-panel');
      if (panel) {
        const willShow = panel.style.display === 'none';
        panel.style.display = willShow ? 'flex' : 'none';
        toggleModelInfoBtn.style.backgroundColor = willShow ? 'rgba(68, 211, 222, 0.3)' : '';
      }
    });
  }

  // Sync the dialog button's initial greyed-out state (default OFF) so
  // the user can see at a glance that element picking is currently
  // disabled. Mirrors updates done elsewhere in the toolbar.
  updateNativeToolbar({
    hasSelection: false,
    selectionCount: 0,
    dialogEnabled: isDialogEnabled,
    cameraEditMode: false,
    cameraSelected: false,
    transformMode: 'translate',
  });

  // -------- Ghost Mode (X-ray) --------
  // Lazy init so it captures the live fragments / gltfRoots references
  // at first click (after models are loaded). Stored on window so the
  // handler always sees the same instance even across re-binds.
  const ensureGhostHandle = (): GhostModeHandle => {
    const w = window as any;
    if (!w.__viewerGhostHandle) {
      w.__viewerGhostHandle = createGhostMode({
        fragments,
        gltfRoots: viewerGltfRoots,
        reapplySelection: async () => {
          if (selectedElements.length > 0) {
            try { await highlightSelectedElements(); } catch { /* noop */ }
          }
        },
        isIsolated: () => isIsolated,
        reapplyIsolate: async () => {
          if (isIsolated && selectedElements.length > 0) {
            try { await isolateElement(); } catch { /* noop */ }
          }
        },
      });
    }
    return w.__viewerGhostHandle as GhostModeHandle;
  };

  const ghostBtn = document.getElementById('btn-ghost');
  if (ghostBtn) {
    ghostBtn.addEventListener('click', async () => {
      const handle = ensureGhostHandle();
      ghostBtn.setAttribute('loading', '');
      try {
        const nowActive = await handle.toggle();
        ghostBtn.setAttribute('label', nowActive ? 'Ghost: ON' : 'Ghost');
        ghostBtn.style.backgroundColor = nowActive ? 'rgba(68, 211, 222, 0.35)' : '';
      } catch (err) {
        console.warn('[ghost] toggle failed', err);
      } finally {
        ghostBtn.removeAttribute('loading');
      }
    });
  }

  // -------- Save / Restore Camera View --------
  const saveViewBtn = document.getElementById('btn-save-view');
  const restoreViewBtn = document.getElementById('btn-restore-view');
  const currentFolderKey = () =>
    makeFolderKey(folderNameParam, modelsToLoad.map((m) => m.id));

  if (saveViewBtn) {
    saveViewBtn.addEventListener('click', () => {
      const view = captureCameraView(world);
      const key = currentFolderKey();
      const ok = saveCameraView(key, view);
      if (ok) {
        showCameraToast('Camera view saved');
        if (restoreViewBtn) restoreViewBtn.removeAttribute('disabled');
        // Quick visual ping on the button.
        saveViewBtn.style.backgroundColor = 'rgba(68, 211, 222, 0.35)';
        window.setTimeout(() => {
          saveViewBtn.style.backgroundColor = '';
        }, 400);
        console.log(`📷 [CameraView] Saved for folderKey="${key}"`, view);
      } else {
        showCameraToast('Failed to save view', 'warn');
      }
    });
    // Shift+click (or right-click) clears the saved view for this group.
    const clearHandler = (ev: Event) => {
      ev.preventDefault();
      const key = currentFolderKey();
      const had = !!loadCameraView(key);
      clearCameraView(key);
      if (restoreViewBtn) restoreViewBtn.setAttribute('disabled', '');
      showCameraToast(had ? 'Saved view cleared' : 'No saved view to clear', 'info');
      console.log(`📷 [CameraView] Cleared for folderKey="${key}"`);
    };
    saveViewBtn.addEventListener('contextmenu', clearHandler);
    saveViewBtn.addEventListener('click', (ev) => {
      if ((ev as MouseEvent).shiftKey) clearHandler(ev);
    }, true);
  }

  if (restoreViewBtn) {
    restoreViewBtn.addEventListener('click', () => {
      const key = currentFolderKey();
      const saved = loadCameraView(key);
      if (!saved) {
        showCameraToast('No saved view yet — click Save View first', 'info');
        restoreViewBtn.setAttribute('disabled', '');
        return;
      }
      const ok = applyCameraView(world, saved, { animate: true });
      if (ok) {
        showCameraToast('Camera view restored');
        console.log(`📷 [CameraView] Restored for folderKey="${key}"`, saved);
      } else {
        showCameraToast('Failed to restore view', 'warn');
      }
    });
  }

}, 600);


// Track panel collapsed state
let isLeftPanelCollapsed = false;
let isRightPanelCollapsed = false;

// Track dialog visibility setting.
// IMPORTANT: defaults to OFF — element selection requires the user to
// explicitly enable "Dialog On" from the toolbar. This keeps the viewer
// safe for casual navigation (orbit/pan/zoom won't accidentally select
// elements and trigger highlight / isolate side-effects).
let isDialogEnabled = false;

// Track camera preview popup
let cameraPreviewPopup: HTMLDivElement | null = null;
let isPreviewVisible = false;

// Create camera preview popup (Picture-in-Picture style overlay)
const createCameraPreviewPopup = () => {
  const popup = document.createElement('div');
  popup.id = 'camera-preview-popup';
  popup.style.cssText = `
    position: fixed;
    top: 80px;
    right: 20px;
    width: 400px;
    height: 300px;
    background: rgba(10, 14, 39, 0.95);
    border: 2px solid #44d3de;
    border-radius: 8px;
    padding: 10px;
    z-index: 10000;
    display: none;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
    pointer-events: auto;
  `;
  
  popup.innerHTML = `
    <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px;">
      <div style="font-weight: bold; color: #44d3de;">📹 Camera Preview</div>
      <button id="close-preview-btn" style="background: none; border: none; color: #44d3de; cursor: pointer; font-size: 1.5rem; padding: 0; line-height: 1;">×</button>
    </div>
    <div id="preview-info" style="font-size: 0.75rem; color: #a0f0f5; margin-bottom: 5px;">Real-time view from virtual camera</div>
    <canvas id="preview-canvas" style="width: 100%; height: calc(100% - 60px); background: #0a0e27; border-radius: 4px; display: block;"></canvas>
  `;
  
  document.body.appendChild(popup);
  
  // Add close button handler
  popup.querySelector('#close-preview-btn')?.addEventListener('click', () => {
    toggleCameraPreview();
  });
  
  return popup;
};

// Initialize preview popup
cameraPreviewPopup = createCameraPreviewPopup();

// Function to toggle camera preview
const toggleCameraPreview = () => {
  if (!cameraPreviewPopup) return;
  
  isPreviewVisible = !isPreviewVisible;
  
  if (isPreviewVisible) {
    if (selectedVirtualCamera === null || selectedVirtualCamera >= virtualCameras.length) {
      console.warn('⚠️ No camera selected for preview');
      isPreviewVisible = false;
      return;
    }
    
    // Show popup
    cameraPreviewPopup.style.display = 'block';
    
    // Set canvas size properly
    const canvas = cameraPreviewPopup.querySelector('#preview-canvas') as HTMLCanvasElement;
    if (canvas) {
      const container = canvas.parentElement;
      if (container) {
        canvas.width = container.clientWidth;
        canvas.height = container.clientHeight;
        console.log('✅ Preview canvas sized:', canvas.width, 'x', canvas.height);
      }
    }
    
    console.log('📹 Camera preview: VISIBLE - Using Picture-in-Picture rendering');
  } else {
    // Hide popup
    cameraPreviewPopup.style.display = 'none';
    console.log('📹 Camera preview: HIDDEN');
  }
};

// Function to render camera preview using Picture-in-Picture on main canvas
const renderCameraPreview = () => {
  if (isPreviewVisible && selectedVirtualCamera !== null && selectedVirtualCamera < virtualCameras.length) {
    const selectedCamera = virtualCameras[selectedVirtualCamera];
    const canvas = cameraPreviewPopup?.querySelector('#preview-canvas') as HTMLCanvasElement;
    
    if (canvas) {
      const ctx = canvas.getContext('2d');
      if (ctx) {
        // Get the main renderer's canvas
        const mainCanvas = world.renderer.three.domElement;
        
        // Save current camera
        const originalCamera = world.camera.three;
        
        // Temporarily switch to virtual camera and render
        try {
          // Update virtual camera
          selectedCamera.camera.matrixWorldNeedsUpdate = true;
          selectedCamera.camera.updateMatrixWorld(true);
          selectedCamera.camera.updateProjectionMatrix();
          
          // Hide helpers temporarily
          const toHide: THREE.Object3D[] = [];
          world.scene.three.traverse((child: any) => {
            if ((child.type?.includes('Helper') || 
                 child.type?.includes('Camera') ||
                 child.userData?.isVirtualCamera) && 
                child.visible) {
              toHide.push(child);
              child.visible = false;
            }
          });
          
          // Clear the preview canvas first to avoid ghosting
          ctx.clearRect(0, 0, canvas.width, canvas.height);
          ctx.fillStyle = '#0a0e27'; // Dark background matching main theme
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          
          // Temporarily replace the camera and render
          (world.camera as any).three = selectedCamera.camera;
          world.renderer.update();
          
          // Copy the rendered frame to preview canvas with proper scaling
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = 'high';
          ctx.drawImage(mainCanvas, 0, 0, canvas.width, canvas.height);
          
          // Restore original camera
          (world.camera as any).three = originalCamera;
          
          // Restore helpers
          toHide.forEach(obj => obj.visible = true);
          
          // Render main view again
          world.renderer.update();
          
        } catch (error) {
          if (!renderCameraPreview.hasLogged) {
            console.error('❌ Preview render failed:', error);
            renderCameraPreview.hasLogged = true;
          }
        }
      }
    }
  }
  
  requestAnimationFrame(renderCameraPreview);
};

// Add tracking property
(renderCameraPreview as any).hasLogged = false;

// Start the preview rendering loop immediately
renderCameraPreview();

// Track virtual cameras
let virtualCameras: Array<{
  camera: THREE.PerspectiveCamera;
  helper: THREE.CameraHelper;
  transformControls: TransformControls;
  marker: THREE.Mesh;
  axesHelper: THREE.AxesHelper; // Visual XYZ axes
}> = [];

// Track camera edit mode
let isCameraEditMode = false;
let selectedVirtualCamera: number | null = null; // Index of selected camera
let currentTransformMode: 'translate' | 'rotate' = 'translate'; // Current transform mode

// Custom drag control state
let isDraggingCamera = false;
let dragPlane = new THREE.Plane();
let dragOffset = new THREE.Vector3();
let dragIntersection = new THREE.Vector3();
const dragRaycaster = new THREE.Raycaster();

// Rotation control state
let rotationStartMouse = new THREE.Vector2();
let rotationStartAngles = { x: 0, y: 0, z: 0 };

// Function to add a virtual camera at current view center
const addVirtualCamera = () => {
  console.log('📹 Adding virtual camera at view center...');
  
  // Get position at view center (in front of current camera)
  const mainCamera = world.camera.three;
  const direction = new THREE.Vector3();
  mainCamera.getWorldDirection(direction);
  
  // Place camera 10 units in front of main camera's view
  const position = mainCamera.position.clone().add(direction.clone().multiplyScalar(10));
  
  // Create virtual camera with same orientation as main camera
  const virtualCamera = new THREE.PerspectiveCamera(60, 16/9, 0.1, 1000);
  virtualCamera.position.copy(position);
  
  // Copy main camera's rotation to virtual camera
  virtualCamera.rotation.copy(mainCamera.rotation);
  virtualCamera.quaternion.copy(mainCamera.quaternion);
  
  virtualCamera.updateMatrixWorld();
  virtualCamera.updateProjectionMatrix();
  
  console.log('📹 Virtual camera created with rotation:', {
    rotation: virtualCamera.rotation.toArray(),
    direction: direction.toArray()
  });
  
  // Create camera icon (sphere marker) - smaller size so it doesn't block the gizmo
  const cameraMarker = new THREE.Mesh(
    new THREE.SphereGeometry(0.2, 16, 16), // Reduced from 0.5 to 0.2
    new THREE.MeshBasicMaterial({ color: 0xff6600, wireframe: false })
  );
  cameraMarker.position.copy(position);
  cameraMarker.rotation.copy(virtualCamera.rotation); // Sync rotation with virtual camera
  cameraMarker.userData.isVirtualCamera = true; // Mark as virtual camera
  cameraMarker.userData.cameraIndex = virtualCameras.length; // Store index
  world.scene.three.add(cameraMarker);
  
  // Create camera helper (frustum visualization)
  const cameraHelper = new THREE.CameraHelper(virtualCamera);
  cameraHelper.visible = true;
  world.scene.three.add(cameraHelper);
  
  // Create a large AxesHelper for visual feedback and dragging
  const axesHelper = new THREE.AxesHelper(8); // Large axes (Red=X, Green=Y, Blue=Z)
  axesHelper.position.copy(position);
  axesHelper.rotation.copy(virtualCamera.rotation); // Sync rotation with virtual camera
  axesHelper.visible = false; // Start hidden
  world.scene.three.add(axesHelper);
  
  // Create transform controls (for moving and rotating)
  const transformControls = new TransformControls(
    world.camera.three,
    world.renderer.three.domElement
  );
  
  // Configure transform controls BEFORE adding to scene
  transformControls.setMode('translate');
  transformControls.setSize(5.0);
  transformControls.setSpace('world');
  
  // CRITICAL: Add TransformControls to scene using the correct method
  // Even though it's not an Object3D, Three.js handles this specially
  // The error message is a warning but it still works
  try {
    // This is the CORRECT way according to Three.js documentation
    (world.scene.three as any).add(transformControls);
    console.log('✅ TransformControls added to scene (method 1)');
  } catch (e) {
    console.warn('⚠️ Method 1 failed, trying alternative...');
    // Alternative: manually add internal objects
    world.scene.three.add(transformControls.getHelper());
    console.log('✅ TransformControls helper added to scene (method 2)');
  }
  
  // Update camera position and rotation when marker moves
  transformControls.addEventListener('change', () => {
    virtualCamera.position.copy(cameraMarker.position);
    virtualCamera.rotation.copy(cameraMarker.rotation);
    virtualCamera.updateMatrixWorld();
    virtualCamera.updateProjectionMatrix();
    cameraHelper.update();
    
    // Update gizmo and plane positions to follow the marker
    const worldPos = new THREE.Vector3();
    cameraMarker.getWorldPosition(worldPos);
    
    if (transformControls._gizmo) {
      transformControls._gizmo.position.copy(worldPos);
      transformControls._gizmo.updateMatrixWorld();
    }
    if (transformControls._plane) {
      transformControls._plane.position.copy(worldPos);
      transformControls._plane.updateMatrixWorld();
    }
  });
  
  // Disable orbit controls when using transform controls
  transformControls.addEventListener('dragging-changed', (event: any) => {
    isTransformDragging = event.value;
    world.camera.controls.enabled = !event.value;
    console.log(`🎮 Transform controls dragging: ${event.value}, orbit controls enabled: ${world.camera.controls.enabled}`);
  });
  
  // Debug: Log when transform controls are interacted with
  transformControls.addEventListener('mouseDown', (event: any) => {
    console.log('🖱️ Transform controls mouse down - orbit controls should be disabled now');
    // Force disable orbit controls immediately
    world.camera.controls.enabled = false;
  });
  
  transformControls.addEventListener('mouseUp', (event: any) => {
    console.log('🖱️ Transform controls mouse up - orbit controls should be re-enabled');
    // Re-enable orbit controls after a short delay
    setTimeout(() => {
      if (!transformControls.dragging) {
        world.camera.controls.enabled = true;
      }
    }, 100);
  });
  
  transformControls.addEventListener('objectChange', () => {
    console.log('🔄 Transform controls: object changed');
  });
  
  console.log('✅ TransformControls event listeners attached');
  
  // Store virtual camera
  const newIndex = virtualCameras.length;
  virtualCameras.push({
    camera: virtualCamera,
    helper: cameraHelper,
    transformControls,
    marker: cameraMarker,
    axesHelper: axesHelper
  });
  
  console.log('✅ Virtual camera added:', {
    position,
    totalCameras: virtualCameras.length
  });
  
  // Don't auto-select - user must click to select in edit mode
  
  return newIndex; // Return index
};

// Function to select a virtual camera
const selectVirtualCamera = (index: number) => {
  if (index < 0 || index >= virtualCameras.length) {
    console.warn('⚠️ Invalid camera index:', index);
    return;
  }
  
  // Deselect previous camera
  if (selectedVirtualCamera !== null && selectedVirtualCamera < virtualCameras.length) {
    const prevCamera = virtualCameras[selectedVirtualCamera];
    (prevCamera.marker.material as THREE.MeshBasicMaterial).color.setHex(0xff6600); // Orange
    prevCamera.axesHelper.visible = false; // Hide axes
    
    // Keep preview open when switching cameras (don't close it)
  }
  
  // Select new camera
  selectedVirtualCamera = index;
  const selectedCamera = virtualCameras[index];
  
  // Change marker color to green
  (selectedCamera.marker.material as THREE.MeshBasicMaterial).color.setHex(0x00ff00);
  
  // Show axes helper for dragging
  selectedCamera.axesHelper.visible = true;
  
  // Always start with translate mode when selecting a camera
  currentTransformMode = 'translate';
  
  console.log(`📹 Selected virtual camera #${index}`, {
    mode: 'Move (Custom Drag)',
    markerPosition: selectedCamera.marker.position.toArray(),
    axesVisible: selectedCamera.axesHelper.visible,
    previewActive: isPreviewVisible
  });
  
  if (isPreviewVisible) {
    console.log('📹 Preview will now show camera #' + index + ' view');
  }
  
  console.log('💡 TIP: Click and drag the camera marker or XYZ axes to move it');
  
  // Update toolbar to show Move mode and enable Move/Rotate button
  updateNativeToolbar({
    hasSelection: selectedIds.length > 0,
    selectionCount: selectedIds.length,
    dialogEnabled: isDialogEnabled,
    cameraEditMode: isCameraEditMode,
    transformMode: currentTransformMode,
    cameraSelected: true
  });
};

// Function to toggle camera edit mode
const toggleCameraEditMode = () => {
  isCameraEditMode = !isCameraEditMode;
  
  console.log(`📹 Toggling camera edit mode to: ${isCameraEditMode}`);
  
  if (isCameraEditMode) {
    // Entering edit mode
    if (virtualCameras.length === 0) {
      console.log('📹 Camera edit mode: ENABLED - Add cameras to start editing');
    } else {
      // Deselect all cameras initially
      if (selectedVirtualCamera !== null && selectedVirtualCamera < virtualCameras.length) {
        const prevCamera = virtualCameras[selectedVirtualCamera];
        prevCamera.transformControls.detach();
        prevCamera.transformControls.enabled = false;
        prevCamera.transformControls.visible = false;
        (prevCamera.marker.material as THREE.MeshBasicMaterial).color.setHex(0xff6600);
      }
      selectedVirtualCamera = null;
      console.log('📹 Camera edit mode: ENABLED - Click a camera marker to select it');
    }
  } else {
    // Exiting edit mode - hide all transform controls
    if (selectedVirtualCamera !== null && selectedVirtualCamera < virtualCameras.length) {
      const selectedCamera = virtualCameras[selectedVirtualCamera];
      selectedCamera.transformControls.detach();
      selectedCamera.transformControls.enabled = false;
      selectedCamera.transformControls.visible = false;
      (selectedCamera.marker.material as THREE.MeshBasicMaterial).color.setHex(0xff6600);
    }
    selectedVirtualCamera = null;
    console.log('📹 Camera edit mode: DISABLED');
  }
  
  console.log('📹 Camera edit mode toggled. New value:', isCameraEditMode);
};

// Function to toggle transform mode for selected virtual camera
const toggleVirtualCameraMode = () => {
  if (selectedVirtualCamera === null || selectedVirtualCamera >= virtualCameras.length) {
    console.warn('⚠️ No camera selected to toggle mode');
    return;
  }
  
  const selectedCamera = virtualCameras[selectedVirtualCamera];
  
  // Toggle mode
  if (currentTransformMode === 'translate') {
    currentTransformMode = 'rotate';
    selectedCamera.transformControls.setMode('rotate');
    console.log('🔄 Virtual camera mode: ROTATE');
  } else {
    currentTransformMode = 'translate';
    selectedCamera.transformControls.setMode('translate');
    console.log('🔄 Virtual camera mode: TRANSLATE');
  }
};

// Function to remove last virtual camera
const removeLastVirtualCamera = () => {
  if (virtualCameras.length === 0) {
    console.warn('⚠️ No virtual cameras to remove');
    return;
  }
  
  const lastIndex = virtualCameras.length - 1;
  const lastCamera = virtualCameras.pop();
  
  if (lastCamera) {
    // Remove from scene
    world.scene.three.remove(lastCamera.helper);
    world.scene.three.remove(lastCamera.marker);
    world.scene.three.remove(lastCamera.transformControls);
    
    // Dispose
    lastCamera.transformControls.dispose();
    lastCamera.marker.geometry.dispose();
    (lastCamera.marker.material as THREE.Material).dispose();
    
    // Update selection if removed camera was selected
    if (selectedVirtualCamera === lastIndex) {
      if (virtualCameras.length > 0) {
        selectVirtualCamera(virtualCameras.length - 1); // Select new last camera
      } else {
        selectedVirtualCamera = null;
      }
    } else if (selectedVirtualCamera !== null && selectedVirtualCamera > lastIndex) {
      selectedVirtualCamera--;
    }
    
    console.log('🗑️ Virtual camera removed. Remaining:', virtualCameras.length);
  }
};

// Debug function to check transform controls status
const debugTransformControls = () => {
  console.log('🔍 Transform Controls Debug:', {
    editMode: isCameraEditMode,
    selectedCameraIndex: selectedVirtualCamera,
    totalCameras: virtualCameras.length,
    transformMode: currentTransformMode
  });
  
  if (selectedVirtualCamera !== null && selectedVirtualCamera < virtualCameras.length) {
    const cam = virtualCameras[selectedVirtualCamera];
    console.log('Selected Camera Details:', {
      enabled: cam.transformControls.enabled,
      visible: cam.transformControls.visible,
      mode: cam.transformControls.mode,
      attached: cam.transformControls.object !== undefined,
      object: cam.transformControls.object,
      domElement: cam.transformControls.domElement,
      camera: cam.transformControls.camera,
      inScene: world.scene.three.children.includes(cam.transformControls),
      gizmoVisible: cam.transformControls._gizmo?.visible,
      planeVisible: cam.transformControls._plane?.visible
    });
    
    // Check if gizmo and plane are in the scene
    console.log('Transform Controls Children:', {
      gizmo: cam.transformControls._gizmo,
      plane: cam.transformControls._plane,
      root: cam.transformControls._root
    });
  }
};

// Expose to console for debugging
(window as any).addVirtualCamera = addVirtualCamera;
(window as any).selectVirtualCamera = selectVirtualCamera;
(window as any).toggleCameraEditMode = toggleCameraEditMode;
(window as any).toggleVirtualCameraMode = toggleVirtualCameraMode;
(window as any).removeLastVirtualCamera = removeLastVirtualCamera;
(window as any).debugTransformControls = debugTransformControls;
(window as any).virtualCameras = virtualCameras;

// 舊的 BUI 面板已移除，使用原生面板

// ========== 創建原生 Selected Element Info 面板 ==========
const createNativeElementInfoPanel = () => {
  const panel = document.createElement('div');
  panel.id = 'native-element-info-panel';
  panel.style.cssText = `
    position: fixed;
    left: 5px;
    top: 5px;
    width: 12rem;
    height: 400px;
    min-width: 8rem;
    max-width: 30rem;
    min-height: 200px;
    max-height: calc(100vh - 10px);
    z-index: 9999;
    background: rgba(10, 14, 39, 0.95);
    border: 1px solid #44d3de;
    border-radius: 8px;
    box-shadow: 0 0 20px rgba(68, 211, 222, 0.3);
    backdrop-filter: blur(10px);
    overflow: hidden;
    display: none;
    flex-direction: column;
  `;
  
  // Header — drag handle + × close (mirrors Clipper panel design)
  const header = document.createElement('div');
  header.style.cssText = `
    padding: 0.5rem;
    border-bottom: 1px solid rgba(68, 211, 222, 0.3);
    color: #44d3de;
    font-weight: 600;
    font-size: 0.75rem;
    cursor: move;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
  `;
  header.setAttribute('data-panel-header', 'true');
  const elTitleSpan = document.createElement('span');
  elTitleSpan.textContent = 'Selected Element Info';
  header.appendChild(elTitleSpan);
  const elCloseBtn = document.createElement('button');
  elCloseBtn.innerHTML = '&times;';
  elCloseBtn.title = 'Close panel';
  elCloseBtn.id = 'btn-element-info-close';
  elCloseBtn.style.cssText = `
    background: transparent; border: none; color: #44d3de;
    cursor: pointer; font-size: 16px; line-height: 1;
    padding: 0 4px; border-radius: 4px;
  `;
  elCloseBtn.onmouseenter = () => { elCloseBtn.style.background = 'rgba(255, 255, 255, 0.12)'; };
  elCloseBtn.onmouseleave = () => { elCloseBtn.style.background = 'transparent'; };
  elCloseBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    panel.style.display = 'none';
    const btn = document.getElementById('btn-toggle-element-info') as HTMLElement | null;
    if (btn) btn.style.backgroundColor = '';
  });
  header.appendChild(elCloseBtn);
  
  // Content container
  const content = document.createElement('div');
  content.id = 'native-element-info-content';
  content.style.cssText = `
    flex: 1;
    overflow: auto;
    padding: 0.5rem;
    font-size: 0.65rem;
    color: #e0e0e0;
  `;
  content.innerHTML = '<div style="color: #999;">Click any element in the 3D scene to view information</div>';
  
  panel.appendChild(header);
  panel.appendChild(content);
  
  return panel;
};

const nativeElementInfoPanel = createNativeElementInfoPanel();

// 更新原生 element info 面板的函數
const updateNativeElementInfoPanel = (info: any) => {
  const content = document.getElementById('native-element-info-content');
  if (!content) return;
  
  const localId = info.localId;
  
  if (!localId) {
    content.innerHTML = '<div style="color: #999;">Click any element in the 3D scene to view information</div>';
    return;
  }
  
  // Build attributes table
  const allAttributesData: any = {};
  allAttributesData['Element ID'] = { value: info.elementId || 'Loading...' };
  allAttributesData['Name'] = { value: info.name || 'Unnamed' };
  allAttributesData['Local ID'] = { value: localId?.toString() || 'N/A' };
  
  if (info.attributes) {
    Object.assign(allAttributesData, info.attributes);
  }
  
  let html = '<div style="margin-bottom: 1rem;">';
  html += '<div style="color: #44d3de; font-weight: 600; margin-bottom: 0.5rem; font-size: 0.7rem;">Attributes</div>';
  html += '<table style="width: 100%; font-size: 0.6rem; border-collapse: collapse;">';
  
  for (const [key, value] of Object.entries(allAttributesData)) {
    const val = (value as any)?.value || value;
    const displayValue = val !== null && val !== undefined ? String(val) : 'N/A';
    html += `
      <tr style="border-bottom: 1px solid rgba(68, 211, 222, 0.1);">
        <td style="padding: 0.25rem; color: #44d3de; font-weight: 500;">${key}</td>
        <td style="padding: 0.25rem; word-break: break-word;">${displayValue}</td>
      </tr>
    `;
  }
  
  html += '</table></div>';
  
  // Property Sets
  if (info.psets && typeof info.psets === 'object') {
    html += '<div>';
    html += '<div style="color: #44d3de; font-weight: 600; margin-bottom: 0.5rem; font-size: 0.7rem;">Property Sets</div>';
    
    const entries = Object.entries(info.psets);
    if (entries.length === 0) {
      html += '<div style="color: #999; font-size: 0.6rem;">No property sets</div>';
    } else {
      for (const [psetName, psetData] of entries) {
        html += `<div style="margin-bottom: 0.75rem;">`;
        html += `<div style="color: #44d3de; font-size: 0.65rem; margin-bottom: 0.25rem;">${psetName}</div>`;
        html += '<table style="width: 100%; font-size: 0.6rem; border-collapse: collapse;">';
        
        const psetEntries = Object.entries(psetData as any);
        for (const [key, value] of psetEntries) {
          const displayValue = value !== null && value !== undefined ? String(value) : 'N/A';
          html += `
            <tr style="border-bottom: 1px solid rgba(68, 211, 222, 0.1);">
              <td style="padding: 0.2rem; color: #999;">${key}</td>
              <td style="padding: 0.2rem; word-break: break-word;">${displayValue}</td>
            </tr>
          `;
        }
        
        html += '</table></div>';
      }
    }
    html += '</div>';
  }
  
  // Copy button
  html += `
    <div style="margin-top: 1rem; padding-top: 0.5rem; border-top: 1px solid rgba(68, 211, 222, 0.3);">
      <button id="btn-copy-info" style="
        width: 100%;
        padding: 0.5rem;
        background: rgba(68, 211, 222, 0.2);
        border: 1px solid #44d3de;
        border-radius: 4px;
        color: #44d3de;
        cursor: pointer;
        font-size: 0.7rem;
        font-weight: 600;
      ">📋 Copy All Info</button>
    </div>
  `;
  
  content.innerHTML = html;
  
  // Bind copy button
  const copyBtn = document.getElementById('btn-copy-info');
  if (copyBtn) {
    copyBtn.addEventListener('click', () => {
      const formatAttrsText = (attrs: any) => {
        if (!attrs) return '(No attributes)';
        const lines: string[] = [];
        for (const [key, value] of Object.entries(attrs)) {
          const val = (value as any)?.value || value;
          lines.push(`${key}: ${val}`);
        }
        return lines.join('\n');
      };
      
      const formatPsetsText = (psets: any) => {
        if (!psets || typeof psets !== 'object') return '(No property sets)';
        const lines: string[] = [];
        for (const [psetName, psetData] of Object.entries(psets)) {
          lines.push(`\n${psetName}:`);
          if (psetData && typeof psetData === 'object') {
            const entries = Object.entries(psetData);
            if (entries.length === 0) {
              lines.push('  (Empty)');
            } else {
              for (const [key, value] of entries) {
                const displayValue = value !== null && value !== undefined ? value : 'N/A';
                lines.push(`  ${key}: ${displayValue}`);
              }
            }
          }
        }
        return lines.join('\n');
      };
      
      const textInfo = [
        '=== Element Information ===',
        `Element ID: ${info.elementId || 'N/A'}`,
        `Name: ${info.name || 'N/A'}`,
        `Local ID: ${localId}`,
        '',
        '=== Attributes ===',
        formatAttrsText(info.attributes),
        '',
        '=== Property Sets ===',
        formatPsetsText(info.psets),
      ].join('\n');
      
      navigator.clipboard.writeText(textInfo);
      console.log('✅ All information copied to clipboard');
      alert('✅ Information copied!');
    });
  }
};


// ========== 創建原生 Model Information 面板 ==========
const createNativeModelInfoPanel = () => {
  const panel = document.createElement('div');
  panel.id = 'native-model-info-panel';
  panel.style.cssText = `
    position: fixed;
    right: 5px;
    top: 5px;
    width: 14rem;
    height: 350px;
    min-width: 10rem;
    max-width: 30rem;
    min-height: 150px;
    max-height: calc(100vh - 10px);
    z-index: 9999;
    background: rgba(10, 14, 39, 0.95);
    border: 1px solid #44d3de;
    border-radius: 8px;
    box-shadow: 0 0 20px rgba(68, 211, 222, 0.3);
    backdrop-filter: blur(10px);
    overflow: hidden;
    display: none;
    flex-direction: column;
  `;
  
  // Header — drag handle + × close (mirrors Clipper panel design)
  const header = document.createElement('div');
  header.style.cssText = `
    padding: 0.5rem;
    border-bottom: 1px solid rgba(68, 211, 222, 0.3);
    color: #44d3de;
    font-weight: 600;
    font-size: 0.75rem;
    cursor: move;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
  `;
  header.setAttribute('data-panel-header', 'true');
  const mdlTitleSpan = document.createElement('span');
  mdlTitleSpan.textContent = 'Model Information';
  header.appendChild(mdlTitleSpan);
  const mdlCloseBtn = document.createElement('button');
  mdlCloseBtn.innerHTML = '&times;';
  mdlCloseBtn.title = 'Close panel';
  mdlCloseBtn.id = 'btn-model-info-close';
  mdlCloseBtn.style.cssText = `
    background: transparent; border: none; color: #44d3de;
    cursor: pointer; font-size: 16px; line-height: 1;
    padding: 0 4px; border-radius: 4px;
  `;
  mdlCloseBtn.onmouseenter = () => { mdlCloseBtn.style.background = 'rgba(255, 255, 255, 0.12)'; };
  mdlCloseBtn.onmouseleave = () => { mdlCloseBtn.style.background = 'transparent'; };
  mdlCloseBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    panel.style.display = 'none';
    const btn = document.getElementById('btn-toggle-model-info') as HTMLElement | null;
    if (btn) btn.style.backgroundColor = '';
  });
  header.appendChild(mdlCloseBtn);
  
  // Content container
  const content = document.createElement('div');
  content.id = 'native-model-info-content';
  content.style.cssText = `
    flex: 1;
    overflow: auto;
    padding: 0.5rem;
    font-size: 0.65rem;
    color: #e0e0e0;
  `;
  
  // 初始內容
  let html = '<div style="margin-bottom: 1rem;">';
  html += '<div style="color: #44d3de; font-weight: 600; margin-bottom: 0.5rem; font-size: 0.7rem;">Info</div>';
  html += '<div style="color: #999; font-size: 0.65rem;">Click any element in the 3D scene to view detailed information on the left panel</div>';
  html += '</div>';
  
  // Categories section
  html += '<div style="margin-bottom: 1rem;">';
  html += '<div style="color: #44d3de; font-weight: 600; margin-bottom: 0.5rem; font-size: 0.7rem;">Categories</div>';
  html += '<select id="category-select" style="width: 100%; padding: 0.4rem; background: rgba(68, 211, 222, 0.1); border: 1px solid #44d3de; border-radius: 4px; color: #e0e0e0; font-size: 0.65rem; margin-bottom: 0.5rem;">';
  html += '<option value="">-- Select Category --</option>';
  
  for (const category of categories) {
    html += `<option value="${category}">${category}</option>`;
  }
  
  html += '</select>';
  html += '<button id="btn-filter-category" disabled style="width: 100%; padding: 0.4rem; background: rgba(68, 211, 222, 0.2); border: 1px solid #44d3de; border-radius: 4px; color: #44d3de; cursor: pointer; font-size: 0.65rem; font-weight: 600;">🔍 Filter by Category</button>';
  html += '</div>';
  
  // Custom Attribute Filter section
  html += '<div style="margin-bottom: 1rem;">';
  html += '<div style="color: #44d3de; font-weight: 600; margin-bottom: 0.5rem; font-size: 0.7rem;">Custom Attribute Filter</div>';
  html += '<input type="text" id="custom-attr-name" placeholder="e.g. Tag, Comments, Type" style="width: 100%; padding: 0.4rem; background: rgba(68, 211, 222, 0.1); border: 1px solid #44d3de; border-radius: 4px; color: #e0e0e0; font-size: 0.65rem; margin-bottom: 0.5rem;">';
  html += '<input type="text" id="custom-attr-value" placeholder="Value (optional, leave empty for any)" style="width: 100%; padding: 0.4rem; background: rgba(68, 211, 222, 0.1); border: 1px solid #44d3de; border-radius: 4px; color: #e0e0e0; font-size: 0.65rem; margin-bottom: 0.5rem;">';
  html += '<button id="btn-filter-custom" style="width: 100%; padding: 0.4rem; background: rgba(68, 211, 222, 0.2); border: 1px solid #44d3de; border-radius: 4px; color: #44d3de; cursor: pointer; font-size: 0.65rem; font-weight: 600;">🎯 Filter by Attribute</button>';
  html += '</div>';
  
  // Show All button
  html += '<button id="btn-show-all" style="width: 100%; padding: 0.4rem; background: rgba(68, 211, 222, 0.1); border: 1px solid #44d3de; border-radius: 4px; color: #44d3de; cursor: pointer; font-size: 0.65rem; font-weight: 600;">👁️ Show All</button>';
  
  content.innerHTML = html;
  
  panel.appendChild(header);
  panel.appendChild(content);
  
  return panel;
};

const nativeModelInfoPanel = createNativeModelInfoPanel();

// 更新原生 model info 面板的函數
const updateNativeModelInfoPanel = (info: any) => {
  const content = document.getElementById('native-model-info-content');
  if (!content) return;
  
  // 保存當前 filter results 的狀態
  const oldResultsDiv = document.getElementById('filter-results');
  const oldResultsContent = document.getElementById('filter-results-content');
  const savedFilterDisplay = oldResultsDiv?.style.display || 'none';
  const savedFilterContent = oldResultsContent?.innerHTML || '';
  
  const hasSelection = info.localId !== null && info.localId !== undefined;
  
  let html = '<div style="margin-bottom: 1rem;">';
  html += '<div style="color: #44d3de; font-weight: 600; margin-bottom: 0.5rem; font-size: 0.7rem;">Info</div>';
  
  if (!hasSelection) {
    html += '<div style="color: #999; font-size: 0.65rem;">Click any element in the 3D scene to view detailed information on the left panel</div>';
  } else {
    html += '<div style="color: #4CAF50; font-weight: 600; font-size: 0.65rem;">✓ Element selected</div>';
    html += '<div style="color: #999; font-size: 0.6rem; margin-top: 0.25rem;">See left panel for details</div>';
  }
  
  html += '</div>';
  
  // Categories section
  html += '<div style="margin-bottom: 1rem;">';
  html += '<div style="color: #44d3de; font-weight: 600; margin-bottom: 0.5rem; font-size: 0.7rem;">Categories</div>';
  html += '<select id="category-select" style="width: 100%; padding: 0.4rem; background: rgba(68, 211, 222, 0.1); border: 1px solid #44d3de; border-radius: 4px; color: #e0e0e0; font-size: 0.65rem; margin-bottom: 0.5rem;">';
  html += '<option value="">-- Select Category --</option>';
  
  for (const category of categories) {
    html += `<option value="${category}">${category}</option>`;
  }
  
  html += '</select>';
  html += '<button id="btn-filter-category" disabled style="width: 100%; padding: 0.4rem; background: rgba(68, 211, 222, 0.2); border: 1px solid #44d3de; border-radius: 4px; color: #44d3de; cursor: pointer; font-size: 0.65rem; font-weight: 600;">🔍 Filter by Category</button>';
  html += '</div>';
  
  // Custom Attribute Filter section
  html += '<div style="margin-bottom: 1rem;">';
  html += '<div style="color: #44d3de; font-weight: 600; margin-bottom: 0.5rem; font-size: 0.7rem;">Custom Attribute Filter</div>';
  html += '<input type="text" id="custom-attr-name" placeholder="e.g. Tag, Comments, Type" style="width: 100%; padding: 0.4rem; background: rgba(68, 211, 222, 0.1); border: 1px solid #44d3de; border-radius: 4px; color: #e0e0e0; font-size: 0.65rem; margin-bottom: 0.5rem;">';
  html += '<input type="text" id="custom-attr-value" placeholder="Value (optional, leave empty for any)" style="width: 100%; padding: 0.4rem; background: rgba(68, 211, 222, 0.1); border: 1px solid #44d3de; border-radius: 4px; color: #e0e0e0; font-size: 0.65rem; margin-bottom: 0.5rem;">';
  html += '<button id="btn-filter-custom" style="width: 100%; padding: 0.4rem; background: rgba(68, 211, 222, 0.2); border: 1px solid #44d3de; border-radius: 4px; color: #44d3de; cursor: pointer; font-size: 0.65rem; font-weight: 600;">🎯 Filter by Attribute</button>';
  html += '</div>';
  
  // Select Similar section
  html += '<div style="margin-bottom: 1rem;">';
  html += '<div style="color: #44d3de; font-weight: 600; margin-bottom: 0.5rem; font-size: 0.7rem;">Select Similar</div>';
  html += `<button id="btn-select-similar" ${hasSelection ? '' : 'disabled'} style="width: 100%; padding: 0.4rem; background: rgba(68, 211, 222, 0.15); border: 1px solid #44d3de; border-radius: 4px; color: #44d3de; cursor: pointer; font-size: 0.65rem; font-weight: 600;">🔍 Select Similar (Family & Type)</button>`;
  html += '<div style="color: #999; font-size: 0.55rem; margin-top: 0.25rem;">Select an element first to enable</div>';
  html += '</div>';
  
  // Show All button
  html += '<button id="btn-show-all" style="width: 100%; padding: 0.4rem; background: rgba(68, 211, 222, 0.1); border: 1px solid #44d3de; border-radius: 4px; color: #44d3de; cursor: pointer; font-size: 0.65rem; font-weight: 600;">👁️ Show All</button>';
  
  // Filter Results section
  html += '<div id="filter-results" style="margin-top: 1rem; padding: 0.75rem; background: rgba(68, 211, 222, 0.05); border: 1px solid rgba(68, 211, 222, 0.2); border-radius: 6px; display: none;">';
  html += '<div style="color: #44d3de; font-weight: 600; margin-bottom: 0.5rem; font-size: 0.7rem;">Filter Results</div>';
  html += '<div id="filter-results-content" style="font-size: 0.65rem; color: #e0e0e0; margin-bottom: 0.75rem;"></div>';
  html += '<div style="display: flex; gap: 0.5rem;">';
  html += '<button id="btn-copy-filter-results" style="flex: 1; padding: 0.4rem; background: rgba(68, 211, 222, 0.2); border: 1px solid #44d3de; border-radius: 4px; color: #44d3de; cursor: pointer; font-size: 0.6rem; font-weight: 600;">📋 Copy IDs</button>';
  html += '<button id="btn-export-filter-results" style="flex: 1; padding: 0.4rem; background: rgba(68, 211, 222, 0.2); border: 1px solid #44d3de; border-radius: 4px; color: #44d3de; cursor: pointer; font-size: 0.6rem; font-weight: 600;">💾 Export JSON</button>';
  html += '</div>';
  html += '</div>';
  
  content.innerHTML = html;
  
  // 恢復 filter results 的狀態
  const newResultsDiv = document.getElementById('filter-results');
  const newResultsContent = document.getElementById('filter-results-content');
  if (newResultsDiv && newResultsContent && savedFilterContent) {
    newResultsDiv.style.display = savedFilterDisplay;
    newResultsContent.innerHTML = savedFilterContent;
  }
  
  // 綁定事件
  bindModelInfoPanelEvents();
};

// 全局變量存儲篩選結果
let lastFilterResults: { [modelId: string]: number[] } = {};

// 綁定 Model Info 面板的事件
const bindModelInfoPanelEvents = () => {
  const categorySelect = document.getElementById('category-select') as HTMLSelectElement;
  const filterBtn = document.getElementById('btn-filter-category') as HTMLButtonElement;
  const customAttrName = document.getElementById('custom-attr-name') as HTMLInputElement;
  const customAttrValue = document.getElementById('custom-attr-value') as HTMLInputElement;
  const filterCustomBtn = document.getElementById('btn-filter-custom') as HTMLButtonElement;
  const showAllBtn = document.getElementById('btn-show-all') as HTMLButtonElement;
  const selectSimilarBtn = document.getElementById('btn-select-similar') as HTMLButtonElement;
  const copyResultsBtn = document.getElementById('btn-copy-filter-results') as HTMLButtonElement;
  const exportResultsBtn = document.getElementById('btn-export-filter-results') as HTMLButtonElement;
  
  if (categorySelect) {
    categorySelect.addEventListener('change', () => {
      if (filterBtn) {
        filterBtn.disabled = !categorySelect.value;
      }
    });
  }
  
  if (filterBtn) {
    filterBtn.addEventListener('click', async () => {
      const category = categorySelect?.value;
      if (!category) return;
      
      filterBtn.textContent = '⏳ Filtering...';
      filterBtn.disabled = true;
      
      try {
        showStatus(`🔍 Filtering by ${category}...`);
        
        let totalCount = 0;
        const visibilityChanges: Array<{model: FRAGS.FragmentsModel, localIds: number[]}> = [];
        
        // 清空上次的篩選結果
        lastFilterResults = {};
        
        // 收集所有模型的可見性變更
        for (const [modelId, currentModel] of fragments.models.list) {
          try {
            const categoryIds = await currentModel.getItemsOfCategories([
              new RegExp(`^${category}$`),
            ]);
            const localIds = categoryIds[category] || [];
            
            visibilityChanges.push({ model: currentModel, localIds });
            lastFilterResults[modelId] = localIds;
            totalCount += localIds.length;
          } catch (error) {
            console.error(`❌ Filter error in model ${modelId}:`, error);
          }
        }
        
        // 批量應用可見性變更
        showStatus(`⚙️ Applying changes...`);
        for (const {model, localIds} of visibilityChanges) {
          await model.setVisible(undefined, false);
          if (localIds.length > 0) {
            await model.setVisible(localIds, true);
          }
        }
        
        await fragments.update(true);
        
        // 顯示篩選結果
        const resultsDiv = document.getElementById('filter-results');
        const resultsContent = document.getElementById('filter-results-content');
        if (resultsDiv && resultsContent) {
          resultsDiv.style.display = 'block';
          resultsContent.innerHTML = `
            <div style="margin-bottom: 0.5rem;">
              <span style="color: #44d3de; font-weight: 600;">Filter Type:</span> Category
            </div>
            <div style="margin-bottom: 0.5rem;">
              <span style="color: #44d3de; font-weight: 600;">Category:</span> ${category}
            </div>
            <div>
              <span style="color: #44d3de; font-weight: 600;">Elements Found:</span> ${totalCount}
            </div>
          `;
        }
        
        showStatus(`✅ Found ${totalCount} elements`, 3000);
        filterBtn.textContent = `✓ ${totalCount} items`;
        setTimeout(() => {
          filterBtn.textContent = '🔍 Filter by Category';
          filterBtn.disabled = false;
        }, 2000);
      } catch (error) {
        console.error('❌ Filter error:', error);
        showStatus('❌ Filter failed', 3000);
        filterBtn.textContent = '❌ Failed';
        setTimeout(() => {
          filterBtn.textContent = '🔍 Filter by Category';
          filterBtn.disabled = false;
        }, 2000);
      }
    });
  }
  
  if (filterCustomBtn) {
    filterCustomBtn.addEventListener('click', async () => {
      const attrName = customAttrName?.value.trim();
      if (!attrName) {
        alert('Please enter attribute name!');
        return;
      }
      
      const attrValue = customAttrValue?.value.trim();
      const attrValueLower = attrValue?.toLowerCase();
      
      filterCustomBtn.textContent = '⏳ Searching...';
      filterCustomBtn.disabled = true;
      
      try {
        const modelsList = Array.from(fragments.models.list);
        const totalModels = modelsList.length;
        let totalCount = 0;
        let processedModels = 0;
        
        showStatus(`🔍 Filtering ${totalModels} models...`);
        
        // 批量處理所有模型的可見性變更
        const visibilityChanges: Array<{model: FRAGS.FragmentsModel, localIds: number[]}> = [];
        
        // 遍歷所有模型
        for (const [modelId, currentModel] of modelsList) {
          try {
            processedModels++;
            showStatus(`🔍 Processing model ${processedModels}/${totalModels}...`);
            
            // 一次性獲取所有 categories 和對應的 localIds
            const modelCategories = await currentModel.getCategories();
            const categoryIdsMap = await currentModel.getItemsOfCategories(
              modelCategories.map(cat => new RegExp(`^${cat}$`))
            );
            
            // 收集所有 localIds
            const allLocalIdsSet = new Set<number>();
            for (const localIds of Object.values(categoryIdsMap)) {
              for (const id of localIds) {
                allLocalIdsSet.add(id);
              }
            }
            
            const allLocalIds = Array.from(allLocalIdsSet);
            if (allLocalIds.length === 0) continue;
            
            // 批量獲取所有元素的屬性和 psets
            const itemsData = await currentModel.getItemsData(allLocalIds, {
              attributesDefault: true,
              relations: {
                IsDefinedBy: { attributes: true, relations: true },
              },
            });
            
            const modelMatchedElements: number[] = [];
            
            // 檢查每個元素
            for (let i = 0; i < itemsData.length; i++) {
              const data = itemsData[i];
              const localId = allLocalIds[i];
              let matched = false;
              
              // 檢查 attributes
              if (attrName in data) {
                const attrData = (data as any)[attrName];
                const actualValue = attrData?.value !== undefined ? String(attrData.value) : String(attrData);
                
                if (!attrValue || actualValue.toLowerCase().includes(attrValueLower!)) {
                  matched = true;
                }
              }
              
              // 檢查 property sets（只在未匹配時檢查）
              if (!matched && data.IsDefinedBy && Array.isArray(data.IsDefinedBy)) {
                for (const pset of data.IsDefinedBy) {
                  if (pset.HasProperties && Array.isArray(pset.HasProperties)) {
                    for (const prop of pset.HasProperties) {
                      if (prop.Name?.value === attrName) {
                        const propValue = prop.NominalValue?.value;
                        if (!attrValue || String(propValue).toLowerCase().includes(attrValueLower!)) {
                          matched = true;
                          break;
                        }
                      }
                    }
                  }
                  if (matched) break;
                }
              }
              
              if (matched) {
                modelMatchedElements.push(localId);
              }
            }
            
            // 收集可見性變更，稍後批量執行
            visibilityChanges.push({
              model: currentModel,
              localIds: modelMatchedElements
            });
            
            lastFilterResults[modelId] = modelMatchedElements;
            
            if (modelMatchedElements.length > 0) {
              totalCount += modelMatchedElements.length;
            }
          } catch (error) {
            console.error(`❌ Filter error in model ${modelId}:`, error);
          }
        }
        
        // 批量應用所有可見性變更
        showStatus(`⚙️ Applying visibility changes...`);
        for (const {model, localIds} of visibilityChanges) {
          await model.setVisible(undefined, false);
          if (localIds.length > 0) {
            await model.setVisible(localIds, true);
          }
        }
        
        await fragments.update(true);
        
        // 顯示篩選結果
        const resultsDiv = document.getElementById('filter-results');
        const resultsContent = document.getElementById('filter-results-content');
        if (resultsDiv && resultsContent) {
          resultsDiv.style.display = 'block';
          resultsContent.innerHTML = `
            <div style="margin-bottom: 0.5rem;">
              <span style="color: #44d3de; font-weight: 600;">Filter Type:</span> Custom Attribute
            </div>
            <div style="margin-bottom: 0.5rem;">
              <span style="color: #44d3de; font-weight: 600;">Attribute:</span> ${attrName}
            </div>
            ${attrValue ? `<div style="margin-bottom: 0.5rem;">
              <span style="color: #44d3de; font-weight: 600;">Value:</span> ${attrValue}
            </div>` : ''}
            <div>
              <span style="color: #44d3de; font-weight: 600;">Elements Found:</span> ${totalCount}
            </div>
          `;
        }
        
        showStatus(`✅ Found ${totalCount} elements`, 3000);
        filterCustomBtn.textContent = `✓ Found ${totalCount}`;
        setTimeout(() => {
          filterCustomBtn.textContent = '🎯 Filter by Attribute';
          filterCustomBtn.disabled = false;
        }, 2000);
      } catch (error) {
        console.error('❌ Custom filter error:', error);
        showStatus('❌ Filter failed', 3000);
        filterCustomBtn.textContent = '❌ Failed';
        setTimeout(() => {
          filterCustomBtn.textContent = '🎯 Filter by Attribute';
          filterCustomBtn.disabled = false;
        }, 2000);
      }
    });
  }
  
  if (showAllBtn) {
    showAllBtn.addEventListener('click', async () => {
      showAllBtn.textContent = '⏳ Loading...';
      showAllBtn.disabled = true;
      
      try {
        showStatus('👁️ Showing all elements...');
        
        for (const [modelId, currentModel] of fragments.models.list) {
          try {
            await currentModel.setVisible(undefined, true);
          } catch (error) {
            console.error(`❌ Show all error in model ${modelId}:`, error);
          }
        }
        
        await fragments.update(true);
        
        // 清空篩選結果並隱藏
        lastFilterResults = {};
        const resultsDiv = document.getElementById('filter-results');
        if (resultsDiv) {
          resultsDiv.style.display = 'none';
        }
        
        showStatus('✅ All elements visible', 3000);
        showAllBtn.textContent = '✓ All shown';
        setTimeout(() => {
          showAllBtn.textContent = '👁️ Show All';
          showAllBtn.disabled = false;
        }, 1500);
      } catch (error) {
        console.error('❌ Show all error:', error);
        showStatus('❌ Failed', 3000);
        showAllBtn.textContent = '❌ Failed';
        setTimeout(() => {
          showAllBtn.textContent = '👁️ Show All';
          showAllBtn.disabled = false;
        }, 1500);
      }
    });
  }
  
  // Copy filter results button
  if (copyResultsBtn) {
    copyResultsBtn.addEventListener('click', () => {
      const text = Object.entries(lastFilterResults)
        .map(([modelId, localIds]) => `${modelId}: [${localIds.join(', ')}]`)
        .join('\n');
      
      navigator.clipboard.writeText(text).then(() => {
        const originalText = copyResultsBtn.textContent;
        copyResultsBtn.textContent = '✓ Copied!';
        showStatus('✅ Copied to clipboard', 2000);
        setTimeout(() => {
          copyResultsBtn.textContent = originalText;
        }, 2000);
      }).catch(err => {
        console.error('❌ Copy failed:', err);
        showStatus('❌ Copy failed', 2000);
      });
    });
  }
  
  // Export filter results button
  if (exportResultsBtn) {
    exportResultsBtn.addEventListener('click', () => {
      const jsonData = JSON.stringify(lastFilterResults, null, 2);
      const blob = new Blob([jsonData], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `filter-results-${Date.now()}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      const originalText = exportResultsBtn.textContent;
      exportResultsBtn.textContent = '✓ Exported!';
      showStatus('✅ JSON exported', 2000);
      setTimeout(() => {
        exportResultsBtn.textContent = originalText;
      }, 2000);
    });
  }
  
  // Select Similar button
  if (selectSimilarBtn) {
    selectSimilarBtn.addEventListener('click', async () => {
      if (!currentElementInfo.localId || !currentElementInfo.modelId) {
        alert('Please select an element first!');
        return;
      }
      
      selectSimilarBtn.textContent = '⏳ Searching...';
      selectSimilarBtn.disabled = true;
      
      try {
        const selectedModel = fragments.models.list.get(currentElementInfo.modelId);
        if (!selectedModel) {
          throw new Error('Cannot find selected element model');
        }
        
        // 獲取選中元素的 Family 和 Type
        const selectedData = await selectedModel.getItemsData([currentElementInfo.localId], {
          attributesDefault: true,
          relations: {
            IsDefinedBy: { attributes: true, relations: true },
          },
        });
        
        if (!selectedData || selectedData.length === 0) {
          throw new Error('Cannot get element data');
        }
        
        const data = selectedData[0];
        const family = (data as any).Family?.value || (data as any).ObjectType?.value;
        const type = (data as any).Type?.value || (data as any).ObjectType?.value;
        
        if (!family && !type) {
          alert('Selected element has no Family or Type attribute!');
          selectSimilarBtn.textContent = '🔍 Select Similar (Family & Type)';
          selectSimilarBtn.disabled = false;
          return;
        }
        
        showStatus(`🔍 Finding similar elements...`);
        
        // 清空上次的篩選結果
        lastFilterResults = {};
        let totalCount = 0;
        const visibilityChanges: Array<{model: FRAGS.FragmentsModel, localIds: number[]}> = [];
        
        // 遍歷所有模型查找相似元素
        for (const [modelId, currentModel] of fragments.models.list) {
          try {
            const modelCategories = await currentModel.getCategories();
            const categoryIdsMap = await currentModel.getItemsOfCategories(
              modelCategories.map(cat => new RegExp(`^${cat}$`))
            );
            
            const allLocalIdsSet = new Set<number>();
            for (const localIds of Object.values(categoryIdsMap)) {
              for (const id of localIds) {
                allLocalIdsSet.add(id);
              }
            }
            
            const allLocalIds = Array.from(allLocalIdsSet);
            if (allLocalIds.length === 0) continue;
            
            const itemsData = await currentModel.getItemsData(allLocalIds, {
              attributesDefault: true,
              relations: {
                IsDefinedBy: { attributes: true, relations: true },
              },
            });
            
            const modelMatchedElements: number[] = [];
            
            for (let i = 0; i < itemsData.length; i++) {
              const itemData = itemsData[i];
              const localId = allLocalIds[i];
              
              const itemFamily = (itemData as any).Family?.value || (itemData as any).ObjectType?.value;
              const itemType = (itemData as any).Type?.value || (itemData as any).ObjectType?.value;
              
              // 匹配 Family 和 Type
              if ((family && itemFamily === family) || (type && itemType === type)) {
                modelMatchedElements.push(localId);
              }
            }
            
            visibilityChanges.push({ model: currentModel, localIds: modelMatchedElements });
            lastFilterResults[modelId] = modelMatchedElements;
            totalCount += modelMatchedElements.length;
          } catch (error) {
            console.error(`❌ Select similar error in model ${modelId}:`, error);
          }
        }
        
        // 批量應用可見性變更
        showStatus(`⚙️ Applying visibility...`);
        for (const {model, localIds} of visibilityChanges) {
          await model.setVisible(undefined, false);
          if (localIds.length > 0) {
            await model.setVisible(localIds, true);
          }
        }
        
        await fragments.update(true);
        
        // 顯示篩選結果
        const resultsDiv = document.getElementById('filter-results');
        const resultsContent = document.getElementById('filter-results-content');
        if (resultsDiv && resultsContent) {
          resultsDiv.style.display = 'block';
          resultsContent.innerHTML = `
            <div style="margin-bottom: 0.5rem;">
              <span style="color: #44d3de; font-weight: 600;">Filter Type:</span> Select Similar
            </div>
            ${family ? `<div style="margin-bottom: 0.5rem;">
              <span style="color: #44d3de; font-weight: 600;">Family:</span> ${family}
            </div>` : ''}
            ${type ? `<div style="margin-bottom: 0.5rem;">
              <span style="color: #44d3de; font-weight: 600;">Type:</span> ${type}
            </div>` : ''}
            <div>
              <span style="color: #44d3de; font-weight: 600;">Elements Found:</span> ${totalCount}
            </div>
          `;
        }
        
        showStatus(`✅ Found ${totalCount} similar elements`, 3000);
        selectSimilarBtn.textContent = `✓ Found ${totalCount}`;
        setTimeout(() => {
          selectSimilarBtn.textContent = '🔍 Select Similar (Family & Type)';
          selectSimilarBtn.disabled = false;
        }, 2000);
      } catch (error) {
        console.error('❌ Select similar error:', error);
        showStatus('❌ Select similar failed', 3000);
        selectSimilarBtn.textContent = '❌ Failed';
        setTimeout(() => {
          selectSimilarBtn.textContent = '🔍 Select Similar (Family & Type)';
          selectSimilarBtn.disabled = false;
        }, 2000);
      }
    });
  }
};


onItemSelected = async () => {
  // Load element information
  if (localId !== null) {
    // Set localId in currentElementInfo
    currentElementInfo.localId = localId;
    currentElementInfo.modelId = clickedModelId;
    
    // Get GlobalId (IFC GUID)
    const globalId = await getElementId();
    currentElementInfo.elementId = globalId || `Local ID: ${localId}`;
    
    // Load name
    const name = await getName();
    currentElementInfo.name = name || 'Unnamed';
    
    // Load attributes
    const attributes = await getAttributes();
    currentElementInfo.attributes = attributes;
    
    // Load property sets
    const rawPsets = await getItemPropertySets();
    if (rawPsets) {
      currentElementInfo.psets = formatItemPsets(rawPsets);
    }
    
    // 啟用 Select Similar 按鈕
    const selectSimilarBtn = document.getElementById('btn-select-similar') as HTMLButtonElement;
    if (selectSimilarBtn) {
      selectSimilarBtn.disabled = false;
    }
    
    // Output to console
    console.log('=== Selected Element Information ===');
    console.log('Element ID (GlobalId):', currentElementInfo.elementId);
    console.log('Local ID:', localId);
    console.log('Name:', currentElementInfo.name);
    console.log('Attributes:', currentElementInfo.attributes);
    console.log('Property Sets:', currentElementInfo.psets);
  }
  
  // Update left element info panel
  updateNativeElementInfoPanel(currentElementInfo);
  // Update right control panel
  updateNativeModelInfoPanel(currentElementInfo);
  // Update native toolbar to enable Isolate button
  updateNativeToolbar({ 
    hasSelection: selectedIds.length > 0,
    selectionCount: selectedIds.length,
    dialogEnabled: isDialogEnabled,
    cameraEditMode: isCameraEditMode,
    transformMode: currentTransformMode,
    cameraSelected: selectedVirtualCamera !== null
  });
  // Fix SVG after update
  setTimeout(() => fixSvgAttributes(), 0);
};

onMultipleItemsSelected = () => {
  // Update center toolbar with multi-selection count
  updateNativeToolbar({ 
    hasSelection: selectedIds.length > 0,
    selectionCount: selectedIds.length,
    dialogEnabled: isDialogEnabled,
    cameraEditMode: isCameraEditMode,
    transformMode: currentTransformMode,
    cameraSelected: selectedVirtualCamera !== null 
  });
  console.log('📊 Multi-selection updated. Total selected:', selectedIds.length);
};

onItemDeselected = () => {
  // Clear element information
  currentElementInfo = {
    elementId: null,
    name: null,
    attributes: null,
    psets: null,
    localId: null,
    modelId: null,
  };
  
  // 禁用 Select Similar 按鈕
  const selectSimilarBtn = document.getElementById('btn-select-similar') as HTMLButtonElement;
  if (selectSimilarBtn) {
    selectSimilarBtn.disabled = true;
  }
  
  // Update left element info panel
  updateNativeElementInfoPanel(currentElementInfo);
  // Update right control panel
  updateNativeModelInfoPanel(currentElementInfo);
  // Update center toolbar to disable Isolate button
  updateNativeToolbar({ 
    hasSelection: false,
    selectionCount: 0,
    dialogEnabled: isDialogEnabled,
    cameraEditMode: isCameraEditMode,
    transformMode: currentTransformMode,
    cameraSelected: selectedVirtualCamera !== null
  });
  // Fix SVG after update
  setTimeout(() => fixSvgAttributes(), 0);
};

// Add native left element info panel to DOM
document.body.append(nativeElementInfoPanel);

// 使面板可拖動
const makePanelDraggable = (panelId: string) => {
  const panel = document.getElementById(panelId) as HTMLElement;
  if (!panel) return;
  
  const header = panel.querySelector('[data-panel-header="true"]') as HTMLElement;
  if (!header) return;
  
  let isDragging = false;
  let startX = 0;
  let startY = 0;
  let startLeft = 0;
  let startTop = 0;
  
  header.addEventListener('mousedown', (e: MouseEvent) => {
    isDragging = true;
    startX = e.clientX;
    startY = e.clientY;
    const rect = panel.getBoundingClientRect();
    startLeft = rect.left;
    startTop = rect.top;
    e.preventDefault();
  });
  
  document.addEventListener('mousemove', (e: MouseEvent) => {
    if (!isDragging) return;
    const deltaX = e.clientX - startX;
    const deltaY = e.clientY - startY;
    panel.style.left = `${startLeft + deltaX}px`;
    panel.style.top = `${startTop + deltaY}px`;
  });
  
  document.addEventListener('mouseup', () => {
    isDragging = false;
  });
};

// 為面板添加自定義 resize handle
const makePanelResizable = (panelId: string) => {
  const panel = document.getElementById(panelId) as HTMLElement;
  if (!panel) {
    console.warn(`⚠️ Panel ${panelId} not found for resize`);
    return;
  }
  
  // 創建 resize handle
  const resizeHandle = document.createElement('div');
  resizeHandle.className = 'custom-resize-handle';
  resizeHandle.style.cssText = `
    position: absolute;
    bottom: 0;
    right: 0;
    width: 20px;
    height: 20px;
    cursor: nwse-resize;
    z-index: 10;
    background: linear-gradient(135deg, transparent 0%, transparent 50%, #44d3de 50%, #44d3de 100%);
    border-bottom-right-radius: 8px;
  `;
  
  panel.appendChild(resizeHandle);
  
  let isResizing = false;
  let startX = 0;
  let startY = 0;
  let startWidth = 0;
  let startHeight = 0;
  
  resizeHandle.addEventListener('mousedown', (e: MouseEvent) => {
    isResizing = true;
    startX = e.clientX;
    startY = e.clientY;
    startWidth = panel.offsetWidth;
    startHeight = panel.offsetHeight;
    e.preventDefault();
    e.stopPropagation();
    console.log(`🔧 Starting resize for ${panelId}`);
  });
  
  document.addEventListener('mousemove', (e: MouseEvent) => {
    if (!isResizing) return;
    
    const deltaX = e.clientX - startX;
    const deltaY = e.clientY - startY;
    
    const newWidth = Math.max(128, Math.min(480, startWidth + deltaX));
    const newHeight = Math.max(200, Math.min(window.innerHeight - 10, startHeight + deltaY));
    
    panel.style.width = `${newWidth}px`;
    panel.style.height = `${newHeight}px`;
  });
  
  document.addEventListener('mouseup', () => {
    if (isResizing) {
      console.log(`✅ Resize ended for ${panelId}`);
    }
    isResizing = false;
  });
  
  console.log(`✅ Panel ${panelId} is now resizable`);
};

// 初始化拖動和縮放
makePanelDraggable('native-element-info-panel');
makePanelResizable('native-element-info-panel');

// Add native right model info panel to DOM
document.body.append(nativeModelInfoPanel);

// 初始化右側面板內容
updateNativeModelInfoPanel(currentElementInfo);

// 初始化右側面板的拖動和縮放
makePanelDraggable('native-model-info-panel');
makePanelResizable('native-model-info-panel');

// 綁定 Model Info 面板的初始事件
bindModelInfoPanelEvents();

// Add native center toolbar to DOM - append directly to body
document.body.append(nativeToolbar);

// Ensure left panel and toolbar are visible with correct styles
setTimeout(() => {
  // Setup left panel and force resize to work
  const leftPanel = document.querySelector('bim-panel#element-info-panel') as HTMLElement;
  if (leftPanel) {
    console.log('✅ Found left panel element:', leftPanel);
    
    // Force set styles
    leftPanel.style.display = 'block';
    leftPanel.style.visibility = 'visible';
    leftPanel.style.position = 'fixed';
    leftPanel.style.left = '5px';
    leftPanel.style.top = '5px';
    leftPanel.style.zIndex = '9999';
    leftPanel.style.width = '12rem';
    leftPanel.style.height = '400px';
    leftPanel.style.minWidth = '8rem';
    leftPanel.style.maxWidth = '30rem';
    leftPanel.style.minHeight = '200px';
    leftPanel.style.maxHeight = 'calc(100vh - 10px)';
    leftPanel.style.overflow = 'hidden';
    leftPanel.style.resize = 'both';
    
    // CRITICAL: Force Shadow DOM to allow resize
    if ((leftPanel as any).shadowRoot) {
      const shadowRoot = (leftPanel as any).shadowRoot;
      const style = document.createElement('style');
      style.textContent = `
        :host {
          resize: both !important;
          overflow: hidden !important;
        }
        div {
          width: 100% !important;
          height: 100% !important;
          overflow: auto !important;
        }
      `;
      shadowRoot.appendChild(style);
      console.log('✅ Shadow DOM style injected for left panel resize');
    }
    
    // Set active attribute
    leftPanel.setAttribute('active', '');
    
    console.log('Left panel styles:', {
      display: window.getComputedStyle(leftPanel).display,
      visibility: window.getComputedStyle(leftPanel).visibility,
      position: window.getComputedStyle(leftPanel).position,
      zIndex: window.getComputedStyle(leftPanel).zIndex,
      resize: window.getComputedStyle(leftPanel).resize,
      overflow: window.getComputedStyle(leftPanel).overflow,
    });
  } else {
    console.error('❌ Cannot find left panel element #element-info-panel');
  }
  
  // Setup right panel and force resize to work
  const rightPanel = document.querySelector('bim-panel.options-menu') as HTMLElement;
  if (rightPanel) {
    console.log('✅ Found right panel element:', rightPanel);
    
    // Force set styles
    rightPanel.style.display = 'block';
    rightPanel.style.visibility = 'visible';
    rightPanel.style.position = 'fixed';
    rightPanel.style.top = '5px';
    rightPanel.style.right = '5px';
    rightPanel.style.zIndex = '10000';
    rightPanel.style.width = '15rem';
    rightPanel.style.height = '500px';
    rightPanel.style.minWidth = '10rem';
    rightPanel.style.maxWidth = '30rem';
    rightPanel.style.minHeight = '300px';
    rightPanel.style.maxHeight = 'calc(100vh - 10px)';
    rightPanel.style.overflow = 'hidden';
    rightPanel.style.resize = 'both';
    
    // CRITICAL: Force Shadow DOM to allow resize
    if ((rightPanel as any).shadowRoot) {
      const shadowRoot = (rightPanel as any).shadowRoot;
      const style = document.createElement('style');
      style.textContent = `
        :host {
          resize: both !important;
          overflow: hidden !important;
        }
        div {
          width: 100% !important;
          height: 100% !important;
          overflow: auto !important;
        }
      `;
      shadowRoot.appendChild(style);
      console.log('✅ Shadow DOM style injected for right panel resize');
    }
    
    console.log('Right panel styles:', {
      display: window.getComputedStyle(rightPanel).display,
      visibility: window.getComputedStyle(rightPanel).visibility,
      resize: window.getComputedStyle(rightPanel).resize,
      overflow: window.getComputedStyle(rightPanel).overflow,
    });
  } else {
    console.error('❌ Cannot find right panel element');
  }

  // Native toolbar is already added and styled, no additional setup needed

  // Make panels draggable
  const makePanelDraggable = (panelId: string) => {
    const panel = document.getElementById(panelId) || document.querySelector(`.${panelId}`) as HTMLElement;
    if (!panel) {
      console.warn(`⚠️ Panel not found: ${panelId}`);
      return;
    }

    let isDragging = false;
    let currentX: number;
    let currentY: number;
    let initialX: number;
    let initialY: number;

    panel.setAttribute('data-draggable', 'true');

    const dragStart = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const rect = panel.getBoundingClientRect();
      
      // Don't drag if clicking on the resize handle (bottom-right 20x20px area)
      const isResizeHandle = (e.clientX > rect.right - 20) && (e.clientY > rect.bottom - 20);
      if (isResizeHandle) return;

      // Don't drag if clicking on interactive elements
      const isInteractive = target.tagName === 'BIM-BUTTON' || 
                           target.tagName === 'BUTTON' ||
                           target.tagName === 'INPUT' ||
                           target.tagName === 'SELECT' ||
                           target.closest('bim-button') ||
                           target.closest('button');
      
      if (isInteractive) {
        console.log('🔘 Clicking on interactive element, not dragging');
        return;
      }
      
      initialX = e.clientX - rect.left;
      initialY = e.clientY - rect.top;
      isDragging = true;
      panel.style.cursor = 'grabbing';
      e.preventDefault();
      e.stopPropagation();
    };

    const dragEnd = () => {
      if (isDragging) {
        isDragging = false;
        if (panel) {
          panel.style.cursor = 'move';
        }
      }
    };

    const drag = (e: MouseEvent) => {
      if (isDragging) {
        e.preventDefault();
        currentX = e.clientX - initialX;
        currentY = e.clientY - initialY;

        // Keep panel within viewport bounds
        const maxX = window.innerWidth - 100;
        const maxY = window.innerHeight - 100;
        
        currentX = Math.max(0, Math.min(currentX, maxX));
        currentY = Math.max(0, Math.min(currentY, maxY));

        panel.style.left = `${currentX}px`;
        panel.style.top = `${currentY}px`;
        panel.style.right = 'auto';
      }
    };

    panel.addEventListener('mousedown', dragStart);
    document.addEventListener('mousemove', drag);
    document.addEventListener('mouseup', dragEnd);
  };

  // Make both panels draggable and resizable (only on desktop)
  makePanelDraggable('element-info-panel');
  makePanelDraggable('options-menu');
  
  // Add custom resize functionality for panels
  const makeResizable = (panelSelector: string) => {
    const panel = document.querySelector(panelSelector) as HTMLElement;
    if (!panel) {
      console.warn(`⚠️ Panel not found for resize: ${panelSelector}`);
      return;
    }
    
    console.log(`🔧 Adding resize handle to ${panelSelector}...`);
    
    // Create resize handle as a fixed overlay
    const resizeHandle = document.createElement('div');
    resizeHandle.className = 'custom-resize-handle';
    resizeHandle.style.cssText = `
      position: fixed;
      width: 25px;
      height: 25px;
      cursor: nwse-resize;
      z-index: 999999;
      background: linear-gradient(135deg, transparent 50%, rgba(68, 211, 222, 0.8) 50%);
      pointer-events: auto;
      border-bottom-right-radius: 8px;
    `;
    
    // Position the handle at panel's bottom-right corner
    const updateHandlePosition = () => {
      const rect = panel.getBoundingClientRect();
      resizeHandle.style.left = (rect.right - 25) + 'px';
      resizeHandle.style.top = (rect.bottom - 25) + 'px';
    };
    
    updateHandlePosition();
    document.body.appendChild(resizeHandle);
    
    let isResizing = false;
    let startX: number, startY: number, startWidth: number, startHeight: number;
    
    resizeHandle.addEventListener('mousedown', (e: MouseEvent) => {
      isResizing = true;
      startX = e.clientX;
      startY = e.clientY;
      startWidth = panel.offsetWidth;
      startHeight = panel.offsetHeight;
      
      e.preventDefault();
      e.stopPropagation();
      
      document.body.style.cursor = 'nwse-resize';
    });
    
    document.addEventListener('mousemove', (e: MouseEvent) => {
      if (!isResizing) {
        // Update handle position when panel moves
        if (panel.offsetWidth > 0) {
          updateHandlePosition();
        }
        return;
      }
      
      const deltaX = e.clientX - startX;
      const deltaY = e.clientY - startY;
      
      const minWidth = 100;
      const maxWidth = 500;
      const minHeight = 150;
      const maxHeight = window.innerHeight - 20;
      
      const newWidth = Math.max(minWidth, Math.min(startWidth + deltaX, maxWidth));
      const newHeight = Math.max(minHeight, Math.min(startHeight + deltaY, maxHeight));
      
      panel.style.width = newWidth + 'px';
      panel.style.height = newHeight + 'px';
      
      updateHandlePosition();
    });
    
    document.addEventListener('mouseup', () => {
      if (isResizing) {
        isResizing = false;
        document.body.style.cursor = '';
      }
    });
    
  };
  
  // Wait for panels to be rendered, then add resize handles
  setTimeout(() => {
    makeResizable('bim-panel#element-info-panel');
    makeResizable('bim-panel.options-menu');
  }, 800);

  // ========== MOBILE RESPONSIVE FOR NATIVE TOOLBAR ==========
  const isMobile = window.innerWidth <= 768;
  
  if (isMobile) {
    const nativeToolbarEl = document.getElementById('native-center-toolbar');
    if (nativeToolbarEl) {
      // Hide group titles
      const titles = nativeToolbarEl.querySelectorAll('div[style*="font-weight: 600"]');
      titles.forEach((title: Element) => {
        (title as HTMLElement).style.display = 'none';
      });
      
      // Remove group backgrounds
      const groups = nativeToolbarEl.querySelectorAll('div[style*="background: rgba(68, 211, 222, 0.1)"]');
      groups.forEach((group: Element) => {
        (group as HTMLElement).style.background = 'none';
        (group as HTMLElement).style.border = 'none';
        (group as HTMLElement).style.padding = '0';
      });
      
      // Hide button labels
      const buttons = nativeToolbarEl.querySelectorAll('bim-button');
      buttons.forEach((button: Element) => {
        const htmlButton = button as HTMLElement;
        
        if ((button as any).shadowRoot) {
          const shadowRoot = (button as any).shadowRoot;
          const labels = shadowRoot.querySelectorAll('bim-label, [slot="label"]');
          labels.forEach((label: Element) => {
            (label as HTMLElement).style.display = 'none';
          });
        }
        
        htmlButton.style.minWidth = '2.5rem';
        htmlButton.style.width = '2.5rem';
        htmlButton.style.height = '2.5rem';
        htmlButton.style.padding = '0.3rem';
      });
    }
  }
  // ========== END MOBILE RESPONSIVE ==========
}, 500);

// 在面板創建後立即修復一次
setTimeout(() => fixSvgAttributes(), 100);

// 修復 SVG 屬性錯誤並隱藏展開/收起圖標
// 使用 MutationObserver 持續監聽並修復
setTimeout(() => {
  const panelElement = document.getElementById("controls-panel");
  if (panelElement) {
    // 修復 Shadow DOM 中的 SVG
    const sections = panelElement.querySelectorAll('bim-panel-section');
    sections.forEach((section) => {
      if (section.shadowRoot) {
        const details = section.shadowRoot.querySelector('details');
        if (details) {
          details.setAttribute('open', '');
        }
      }
    });
    
    // 使用 MutationObserver 監聽所有變化，立即修復 SVG
    const observer = new MutationObserver(() => {
      // 立即修復所有 SVG（使用 requestAnimationFrame 確保在渲染後執行）
      requestAnimationFrame(() => {
        fixSvgAttributes();
      });
    });
    
    observer.observe(panelElement, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['height', 'width'],
    });
    
    // 監聽整個文檔的變化（捕獲 categories dropdown 更新）
    const globalObserver = new MutationObserver(() => {
      requestAnimationFrame(() => {
        fixSvgAttributes();
      });
    });
    
    globalObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['height', 'width'],
    });
    
    // 定期修復（作為備份機制）
    setInterval(() => {
      fixSvgAttributes();
    }, 500);
    
    // 初始修復
    requestAnimationFrame(() => {
      fixSvgAttributes();
    });
  }
}, 500);


/* MD
  And we will make some logic that adds a button to the screen when the user is visiting our app from their phone, allowing to show or hide the menu. Otherwise, the menu would make the app unusable.
*/

const button = BUI.Component.create<BUI.PanelSection>(() => {
  const onClick = () => {
    if (panel.classList.contains("options-menu-visible")) {
      panel.classList.remove("options-menu-visible");
    } else {
      panel.classList.add("options-menu-visible");
    }
  };

  return BUI.html`
    <bim-button class="phone-menu-toggler" icon="solar:settings-bold"
      @click=${onClick}>
    </bim-button>
  `;
});

document.body.append(button);

/* MD
  ### ⏱️ Measuring the performance (optional)
  We'll use the [Stats.js](https://github.com/mrdoob/stats.js) to measure the performance of our app. We will add it to the top left corner of the viewport. This way, we'll make sure that the memory consumption and the FPS of our app are under control.
*/

const stats = new Stats();
stats.showPanel(2);
document.body.append(stats.dom);
stats.dom.style.left = "0px";
stats.dom.style.top = "0px";

// 創建全局狀態欄
const createStatusBar = () => {
  const statusBar = document.createElement('div');
  statusBar.id = 'global-status-bar';
  statusBar.style.cssText = `
    position: fixed;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    background: rgba(10, 14, 39, 0.95);
    border: 1px solid #44d3de;
    border-top: none;
    border-radius: 0 0 8px 8px;
    padding: 0.5rem 1.5rem;
    z-index: 99999;
    color: #44d3de;
    font-size: 0.75rem;
    font-weight: 600;
    box-shadow: 0 4px 20px rgba(68, 211, 222, 0.3);
    backdrop-filter: blur(10px);
    display: none;
    min-width: 200px;
    text-align: center;
  `;
  return statusBar;
};

const globalStatusBar = createStatusBar();
document.body.append(globalStatusBar);

// 狀態欄輔助函數
const showStatus = (message: string, duration: number = 0) => {
  globalStatusBar.textContent = message;
  globalStatusBar.style.display = 'block';
  
  if (duration > 0) {
    setTimeout(() => {
      globalStatusBar.style.display = 'none';
    }, duration);
  }
};

const hideStatus = () => {
  globalStatusBar.style.display = 'none';
};
stats.dom.style.zIndex = "9998"; // 低於面板的 z-index

/* MD
  ### 🎛️ Map Controls
  Add controls to switch between different view modes:
*/

// 創建地圖控制按鈕（僅在 Mapbox 已初始化時顯示）
// 使用 setTimeout 確保在 DOM 完全準備好後再創建控制按鈕
setTimeout(() => {
  if (mapManager) {
    const mapControls = document.getElementById("map-controls");
    if (!mapControls) {
      console.warn("找不到地圖控制容器 #map-controls");
      return;
    }
    
    const [mapControlsPanel] = BUI.Component.create<BUI.PanelSection, {}>(() => {
      const onToggle3D = () => {
        mapManager?.setViewMode("3d-only");
      };

      const onToggleMap = () => {
        mapManager?.setViewMode("map-only");
      };

      const onToggleSplit = () => {
        mapManager?.setViewMode("split");
      };

      const onToggleOverlay = () => {
        mapManager?.setViewMode("overlay");
      };

      return BUI.html`
        <bim-panel label="地圖控制" active>
          <bim-panel-section fixed label="視圖模式">
            <bim-button label="僅 3D 場景" @click=${onToggle3D}></bim-button>
            <bim-button label="僅地圖" @click=${onToggleMap}></bim-button>
            <bim-button label="分屏顯示" @click=${onToggleSplit}></bim-button>
            <bim-button label="地圖覆蓋" @click=${onToggleOverlay}></bim-button>
          </bim-panel-section>
        </bim-panel>
      `;
    });

    mapControls.append(mapControlsPanel);
    
    // 默認使用分屏模式
    mapManager.setViewMode("split");
    
    // 將 mapManager 暴露到全局，方便調試
    (window as any).mapManager = mapManager;
    
    // 強制顯示地圖（確保可見）
    const mapContainerEl = document.getElementById("map-container");
    if (mapContainerEl) {
      mapContainerEl.style.display = "block";
      mapContainerEl.style.visibility = "visible";
      mapContainerEl.style.opacity = "1";
      mapContainerEl.style.zIndex = "1";
    }
    
    // 確保地圖調整大小並顯示
    setTimeout(() => {
      const map = mapManager?.getMap();
      if (map) {
        map.resize();
        console.log("✅ 地圖已調整大小");
        
        // 檢查地圖是否真的可見
        const mapCanvas = mapContainerEl?.querySelector("canvas");
        if (mapCanvas) {
          console.log("✅ 地圖 Canvas 已找到:", mapCanvas);
          console.log("📐 Canvas 尺寸:", {
            width: mapCanvas.width,
            height: mapCanvas.height,
            styleWidth: mapCanvas.style.width,
            styleHeight: mapCanvas.style.height
          });
        } else {
          console.warn("⚠️ 未找到地圖 Canvas，地圖可能尚未完全載入");
        }
      }
    }, 500);
    
    // 添加全局測試函數
    (window as any).showMap = () => {
      if (mapManager) {
        mapManager.setViewMode("map-only");
        console.log("✅ 已切換到僅地圖模式");
      }
    };
    
    (window as any).showSplit = () => {
      if (mapManager) {
        mapManager.setViewMode("split");
        console.log("✅ 已切換到分屏模式");
      }
    };
    
    console.log("💡 提示：您可以在控制台使用以下命令：");
    console.log("  - showMap() - 只顯示地圖");
    console.log("  - showSplit() - 分屏顯示");
    console.log("  - mapManager.getMap().getCenter() - 查看當前地圖中心");
    console.log("  - mapManager.getMap().getZoom() - 查看當前縮放級別");
    console.log("  - mapManager.setModelLocation(經度, 緯度, '名稱') - 設置模型位置");
    console.log("  - mapManager.setViewMode('map-only') - 只顯示地圖");
    console.log("  - mapManager.setViewMode('split') - 分屏顯示");
  }
}, 500);
world.renderer.onBeforeUpdate.add(() => stats.begin());
world.renderer.onAfterUpdate.add(() => stats.end());

/* MD
  ### 🎉 Congratulations!
  You've successfully mastered the art of retrieving information from your FragmentsModel! 🚀
  
  With this knowledge, you're now equipped to explore, manipulate, and extract valuable insights from your BIM models. Keep experimenting and building amazing applications! 💡
*/
